import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Cell, LabelList, LineChart, Line
} from 'recharts';
import {
  ChevronRight, Brain, Sparkles, Target, AlertTriangle,
  TrendingUp, Quote, Layers, Crosshair, ArrowRight, Calendar
} from 'lucide-react';
import config from '../../config';
import infectoData from './data/infectologia.json';
import './AnalisisMente.css';

// Áreas disponibles para análisis Mente Examinador
// Si tiene narrative=true, hay JSON estructurado en data/{slug}.json
const AREAS = [
  { id: 'Infectología', label: 'Infectología', icon: '🦠', specialist: 'infectólogo', enabled: true, narrative: infectoData },
  { id: 'Ginecología', label: 'Ginecología', icon: '🌸', specialist: 'ginecólogo', enabled: false },
  { id: 'Pediatría', label: 'Pediatría', icon: '👶', specialist: 'pediatra', enabled: false },
  { id: 'Neonatología', label: 'Neonatología', icon: '🍼', specialist: 'neonatólogo', enabled: false },
  { id: 'Obstetricia', label: 'Obstetricia', icon: '🤰', specialist: 'obstetra', enabled: false },
  { id: 'Traumatología', label: 'Traumatología', icon: '🦴', specialist: 'traumatólogo', enabled: false },
  { id: 'Cardiología', label: 'Cardiología', icon: '❤️', specialist: 'cardiólogo', enabled: false },
  { id: 'Hematología', label: 'Hematología', icon: '🩸', specialist: 'hematólogo', enabled: false },
  { id: 'Nefrología', label: 'Nefrología', icon: '🫘', specialist: 'nefrólogo', enabled: false },
  { id: 'Neumología', label: 'Neumología', icon: '🫁', specialist: 'neumólogo', enabled: false },
  { id: 'Endocrinología', label: 'Endocrinología', icon: '⚗️', specialist: 'endocrinólogo', enabled: false },
  { id: 'Neurología', label: 'Neurología', icon: '🧠', specialist: 'neurólogo', enabled: false },
];

// Premium palette
const PALETTE = {
  success: { fill: '#10b981', text: '#059669' },
  danger:  { fill: '#f43f5e', text: '#e11d48' },
  warning: { fill: '#f59e0b', text: '#d97706' },
  info:    { fill: '#6366f1', text: '#4f46e5' },
  purple:  { fill: '#a855f7', text: '#9333ea' },
};

const accuracyColor = (acc) => {
  const n = parseFloat(acc) || 0;
  if (n >= 70) return PALETTE.success.fill;
  if (n >= 50) return PALETTE.info.fill;
  if (n >= 30) return PALETTE.warning.fill;
  return PALETTE.danger.fill;
};

const probColor = (p) => {
  if (p >= 80) return PALETTE.danger.fill;
  if (p >= 60) return PALETTE.warning.fill;
  if (p >= 40) return PALETTE.info.fill;
  return PALETTE.success.fill;
};

export default function MenteExaminadorView({ darkMode = false }) {
  const [selectedAreaId, setSelectedAreaId] = useState('Infectología');
  const selectedArea = AREAS.find(a => a.id === selectedAreaId);
  const areaData = selectedArea?.narrative;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [userAnswers, setUserAnswers] = useState([]);
  const [view, setView] = useState('macro');
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [selectedSubtopic, setSelectedSubtopic] = useState(null);

  const cardClass = darkMode ? 'am-card-dark' : 'am-card-light';
  const text = darkMode ? 'text-slate-100' : 'text-slate-900';
  const subText = darkMode ? 'text-slate-400' : 'text-slate-500';
  const muted = darkMode ? 'text-slate-500' : 'text-slate-400';
  const innerBg = darkMode ? 'bg-slate-900/40' : 'bg-slate-50/80';
  const hairline = darkMode ? 'am-hairline-dark' : 'am-hairline-light';

  // Reset drilldown al cambiar de área
  useEffect(() => {
    setView('macro');
    setSelectedTopic(null);
    setSelectedSubtopic(null);
  }, [selectedAreaId]);

  // Fetch data al cambiar de área (solo si está enabled)
  useEffect(() => {
    if (!selectedArea?.enabled) {
      setQuestions([]);
      setUserAnswers([]);
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const apiUrl = config.getApiUrl();
        const areaParam = encodeURIComponent(selectedAreaId);
        const [qRes, sRes] = await Promise.all([
          fetch(`${apiUrl}/questions/filter?areas=${areaParam}`),
          fetch(`${apiUrl}/exam-sessions/9`),
        ]);
        if (!qRes.ok) throw new Error('Error preguntas: ' + qRes.status);
        const qData = await qRes.json();
        const sData = sRes.ok ? await sRes.json() : { answers: [] };
        setQuestions(Array.isArray(qData) ? qData.filter(q => q.year && q.year.startsWith('RM')) : []);
        setUserAnswers((sData.answers || []).filter(a => a.area === selectedAreaId));
      } catch (e) {
        console.error('Error cargando Mente Examinador:', e);
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedAreaId, selectedArea]);

  // ============================================================================
  // ANÁLISIS MACRO
  // ============================================================================
  const macroAnalysis = useMemo(() => {
    if (!questions.length) return null;

    const total = questions.length;

    // Frase favorita
    const phrases = {
      eleccion: 0, probable: 0, conducta: 0, agente: 0, tratamiento: 0, diagnostico: 0,
    };
    for (const q of questions) {
      const t = (q.question_text || '').toLowerCase();
      if (t.includes('de elección') || t.includes('de eleccion')) phrases.eleccion++;
      if (t.includes('más probable') || t.includes('mas probable')) phrases.probable++;
      if (t.includes('conducta')) phrases.conducta++;
      if (t.includes('agente') || t.includes('etiolog')) phrases.agente++;
      if (t.includes('tratamiento') || t.includes('fármaco') || t.includes('farmaco')) phrases.tratamiento++;
      if (t.includes('diagn')) phrases.diagnostico++;
    }

    // Distribución por tema con accuracy del usuario
    const topicMap = {};
    for (const q of questions) {
      const topic = q.topic || 'Sin tema';
      if (!topicMap[topic]) topicMap[topic] = { topic, total: 0, years: new Set() };
      topicMap[topic].total++;
      topicMap[topic].years.add(q.year);
    }

    // Cruce con userAnswers (RM 2025) — accuracy por topic
    const userByTopic = {};
    for (const a of userAnswers) {
      const topic = a.topic || 'Sin tema';
      if (!userByTopic[topic]) userByTopic[topic] = { correct: 0, total: 0 };
      userByTopic[topic].total++;
      if (a.status === 'correct') userByTopic[topic].correct++;
    }

    const topics = Object.values(topicMap)
      .map(t => {
        const yearsArr = Array.from(t.years).sort();
        const lastYear = yearsArr[yearsArr.length - 1];
        const lastYearNum = parseInt(lastYear?.replace('RM ', '') || 0);
        const gap = 2026 - lastYearNum;
        const userPerf = userByTopic[t.topic] || { correct: 0, total: 0 };
        const userAcc = userPerf.total > 0 ? Math.round((userPerf.correct / userPerf.total) * 100) : null;
        return {
          topic: t.topic,
          total: t.total,
          lastYear,
          gap,
          userTotal: userPerf.total,
          userCorrect: userPerf.correct,
          userAccuracy: userAcc,
          shortName: t.topic.length > 18 ? t.topic.substring(0, 17) + '…' : t.topic,
        };
      })
      .sort((a, b) => b.total - a.total);

    // Casos clínicos vs directas
    const casos = questions.filter(q => (q.case_description || '').length > 50).length;
    const directas = total - casos;

    return { total, phrases, topics, casos, directas };
  }, [questions, userAnswers]);

  // ============================================================================
  // ANÁLISIS MESO (cuando selectedTopic)
  // ============================================================================
  const mesoAnalysis = useMemo(() => {
    if (!selectedTopic || !questions.length) return null;
    const topicQs = questions.filter(q => q.topic === selectedTopic);

    // Distribución temporal
    const byYear = {};
    for (let y = 2010; y <= 2025; y++) {
      byYear[`RM ${y}`] = { year: y, count: 0, subtemas: [] };
    }
    for (const q of topicQs) {
      if (byYear[q.year]) {
        byYear[q.year].count++;
        if (q.subtopic_specific) byYear[q.year].subtemas.push(q.subtopic_specific);
      }
    }
    const timeline = Object.values(byYear);

    // Subtemas con gap
    const subMap = {};
    for (const q of topicQs) {
      const sub = q.subtopic_specific || q.subtopic || 'Sin subtema';
      if (!subMap[sub]) subMap[sub] = { subtopic: sub, count: 0, years: new Set() };
      subMap[sub].count++;
      subMap[sub].years.add(q.year);
    }

    const userBySubtopic = {};
    for (const a of userAnswers.filter(a => a.topic === selectedTopic)) {
      const sub = a.subtopic_specific || a.subtopic || 'Sin subtema';
      if (!userBySubtopic[sub]) userBySubtopic[sub] = { correct: 0, total: 0 };
      userBySubtopic[sub].total++;
      if (a.status === 'correct') userBySubtopic[sub].correct++;
    }

    const subtopics = Object.values(subMap).map(s => {
      const yearsArr = Array.from(s.years).sort();
      const lastY = yearsArr[yearsArr.length - 1];
      const gap = 2026 - parseInt(lastY?.replace('RM ', '') || 0);
      const u = userBySubtopic[s.subtopic] || { correct: 0, total: 0 };
      return {
        subtopic: s.subtopic,
        count: s.count,
        lastYear: lastY,
        gap,
        years: yearsArr,
        userTotal: u.total,
        userCorrect: u.correct,
      };
    }).sort((a, b) => b.gap - a.gap);

    return { topicQs, timeline, subtopics, total: topicQs.length };
  }, [selectedTopic, questions, userAnswers]);

  // ============================================================================
  // ANÁLISIS DATA — toda la evidencia cruda
  // ============================================================================
  const dataAnalysis = useMemo(() => {
    if (!questions.length) return null;

    // Matriz tema × año
    const years = [];
    for (let y = 2010; y <= 2025; y++) years.push(`RM ${y}`);
    const topicSet = new Set();
    questions.forEach(q => topicSet.add(q.topic));
    const topicsList = Array.from(topicSet).sort();
    const matrix = {};
    for (const t of topicsList) {
      matrix[t] = { topic: t, total: 0 };
      for (const y of years) matrix[t][y] = 0;
    }
    for (const q of questions) {
      if (matrix[q.topic] && matrix[q.topic][q.year] !== undefined) {
        matrix[q.topic][q.year]++;
        matrix[q.topic].total++;
      }
    }
    const topicMatrix = Object.values(matrix).sort((a, b) => b.total - a.total);

    // Distribución por año
    const yearTotals = years.map(y => ({
      year: y,
      total: questions.filter(q => q.year === y).length,
    }));

    // Todos los subtemas con gap
    const subMap = {};
    for (const q of questions) {
      const sub = q.subtopic_specific || q.subtopic || 'Sin subtema';
      if (!subMap[sub]) subMap[sub] = { subtopic: sub, topic: q.topic, count: 0, years: new Set() };
      subMap[sub].count++;
      subMap[sub].years.add(q.year);
    }
    const allSubtopics = Object.values(subMap).map(s => {
      const yarr = Array.from(s.years).sort();
      const last = yarr[yarr.length - 1];
      const gap = 2026 - parseInt(last?.replace('RM ', '') || 0);
      return { ...s, lastYear: last, years: yarr, gap };
    }).sort((a, b) => b.gap - a.gap || b.count - a.count);

    // Distribución por ángulo
    const angles = { DIAGNOSTICO: 0, TRATAMIENTO: 0, AGENTE: 0, LOCALIZACION: 0, CICLO: 0, CLASIFICACION: 0, OTRO: 0 };
    for (const q of questions) {
      const t = (q.question_text || '').toLowerCase();
      if (t.includes('tratamiento') || t.includes('fármaco') || t.includes('farmaco')) angles.TRATAMIENTO++;
      else if (t.includes('diagn') || t.includes('sospecha')) angles.DIAGNOSTICO++;
      else if (t.includes('localiz') || t.includes('dónde') || t.includes('donde')) angles.LOCALIZACION++;
      else if (t.includes('agente') || t.includes('etiolog')) angles.AGENTE++;
      else if (t.includes('ciclo') || t.includes('fase')) angles.CICLO++;
      else if (t.includes('clasifi')) angles.CLASIFICACION++;
      else angles.OTRO++;
    }

    // Casos vs directas por tema
    const casosTema = topicsList.map(t => {
      const tQs = questions.filter(q => q.topic === t);
      const casos = tQs.filter(q => (q.case_description || '').length > 50).length;
      return { topic: t, total: tQs.length, casos, directas: tQs.length - casos };
    }).sort((a, b) => b.total - a.total);

    // Pool global de distractores (top 30 más usados como wrong)
    const distMap = {};
    for (const q of questions) {
      for (const opt of (q.options || [])) {
        if (!opt.correct) {
          const txt = opt.option_text;
          if (!distMap[txt]) distMap[txt] = { text: txt, count: 0, topics: new Set() };
          distMap[txt].count++;
          distMap[txt].topics.add(q.topic);
        }
      }
    }
    const topDistractors = Object.values(distMap)
      .map(d => ({ ...d, topics: Array.from(d.topics) }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 30);

    return { topicMatrix, years, yearTotals, allSubtopics, angles, casosTema, topDistractors };
  }, [questions]);

  // ============================================================================
  // ANÁLISIS MICRO (cuando selectedSubtopic)
  // ============================================================================
  const microAnalysis = useMemo(() => {
    if (!selectedSubtopic || !questions.length) return null;
    const subQs = questions.filter(q =>
      (q.subtopic_specific || q.subtopic) === selectedSubtopic
    );

    // Pool de distractores
    const distractorMap = {};
    for (const q of subQs) {
      for (const opt of (q.options || [])) {
        if (!opt.correct) {
          const txt = opt.option_text;
          if (!distractorMap[txt]) distractorMap[txt] = { text: txt, count: 0, years: new Set() };
          distractorMap[txt].count++;
          distractorMap[txt].years.add(q.year);
        }
      }
    }
    const distractors = Object.values(distractorMap)
      .map(d => ({ ...d, years: Array.from(d.years).sort() }))
      .sort((a, b) => b.count - a.count);

    return { subQs, distractors };
  }, [selectedSubtopic, questions]);

  // ============================================================================
  // NAVIGATION
  // ============================================================================
  const goToMeso = (topic) => {
    setSelectedTopic(topic);
    setSelectedSubtopic(null);
    setView('meso');
  };
  const goToMicro = (subtopic) => {
    setSelectedSubtopic(subtopic);
    setView('micro');
  };

  // ============================================================================
  // LEVEL STEPPER — indicador visual de jerarquía MACRO/MESO/MICRO + DATA
  // ============================================================================
  const LevelStepper = () => {
    const levels = [
      {
        id: 'macro',
        num: '01',
        title: 'MACRO',
        subtitle: 'Área completa',
        description: 'Lectura del experto + análisis estratégico por tema',
        active: view === 'macro',
        done: (view === 'meso' || view === 'micro') && view !== 'data',
        clickable: true,
        onClick: () => { setView('macro'); setSelectedTopic(null); setSelectedSubtopic(null); },
      },
      {
        id: 'meso',
        num: '02',
        title: 'MESO',
        subtitle: 'Por tema',
        description: selectedTopic ? `Drill en ${selectedTopic}` : 'Click un tema para activar',
        active: view === 'meso',
        done: view === 'micro',
        clickable: !!selectedTopic,
        onClick: () => { if (selectedTopic) { setView('meso'); setSelectedSubtopic(null); } },
      },
      {
        id: 'micro',
        num: '03',
        title: 'MICRO',
        subtitle: 'Por subtema',
        description: selectedSubtopic ? `Predicción ${selectedSubtopic}` : 'Click un subtema para activar',
        active: view === 'micro',
        done: false,
        clickable: !!selectedSubtopic,
      },
    ];

    return (
      <div className={`${cardClass} am-noise rounded-2xl p-5 mb-6`}>
        <div className={`am-eyebrow mb-4 ${muted}`}>
          <Layers className="w-3 h-3 inline mr-2" />
          NIVEL DE ANÁLISIS
        </div>
        <div className="flex items-stretch gap-2">
          {levels.map((l, i) => {
            const accent = l.active ? PALETTE.danger.fill : l.done ? PALETTE.success.fill : '#64748b';
            return (
              <React.Fragment key={l.id}>
                <button
                  onClick={l.clickable ? l.onClick : undefined}
                  disabled={!l.clickable}
                  className={`am-focus flex-1 text-left p-3 rounded-xl transition-all relative ${
                    l.active
                      ? darkMode ? 'bg-slate-700/40' : 'bg-slate-100'
                      : l.clickable
                        ? darkMode ? 'hover:bg-slate-800/40' : 'hover:bg-slate-50'
                        : 'opacity-60 cursor-not-allowed'
                  }`}
                  style={{ border: `1px solid ${accent}${l.active ? '50' : '20'}` }}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className={`am-numeric text-xs px-1.5 py-0.5 rounded`}
                      style={{ background: `${accent}25`, color: accent }}
                    >
                      {l.num}
                    </span>
                    <span className={`am-eyebrow ${l.active ? text : muted}`} style={l.active ? { color: accent } : {}}>
                      {l.title}
                    </span>
                    {l.done && !l.active && (
                      <span className="am-mono text-[10px] text-emerald-500">✓</span>
                    )}
                  </div>
                  <div className={`am-sans text-sm font-semibold mb-0.5 ${l.active ? text : subText}`}>
                    {l.subtitle}
                  </div>
                  <div className={`am-mono text-[10px] leading-snug line-clamp-2 ${muted}`}>
                    {l.description}
                  </div>
                </button>
                {i < levels.length - 1 && (
                  <div className="flex items-center px-1">
                    <ChevronRight className={`w-4 h-4 ${muted}`} />
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    );
  };

  // ============================================================================
  // BREADCRUMB
  // ============================================================================
  const Breadcrumb = () => (
    <div className={`flex items-center gap-2 mb-8 am-mono text-xs`}>
      <button
        onClick={() => { setView('macro'); setSelectedTopic(null); setSelectedSubtopic(null); }}
        className={`am-focus uppercase tracking-widest hover:underline ${
          view === 'macro' ? text + ' font-bold' : subText
        }`}
      >
        Infectología
      </button>
      {selectedTopic && (
        <>
          <ChevronRight className={`w-3 h-3 ${muted}`} />
          <button
            onClick={() => { setView('meso'); setSelectedSubtopic(null); }}
            className={`am-focus uppercase tracking-widest hover:underline truncate max-w-[200px] ${
              view === 'meso' ? text + ' font-bold' : subText
            }`}
          >
            {selectedTopic}
          </button>
        </>
      )}
      {selectedSubtopic && view === 'micro' && (
        <>
          <ChevronRight className={`w-3 h-3 ${muted}`} />
          <span className={text + ' uppercase tracking-widest font-bold truncate max-w-[200px]'}>
            {selectedSubtopic}
          </span>
        </>
      )}
    </div>
  );

  // ============================================================================
  // AREA SELECTOR — siempre visible arriba
  // ============================================================================
  const AreaSelector = () => (
    <div className="mb-8">
      <div className={`am-eyebrow mb-3 ${muted}`}>SELECCIONA ÁREA · {AREAS.filter(a => a.enabled).length}/{AREAS.length} DISPONIBLES</div>
      <div className="am-scroll flex gap-2 overflow-x-auto pb-2 -mx-1 px-1">
        {AREAS.map((a) => {
          const isActive = a.id === selectedAreaId;
          return (
            <button
              key={a.id}
              onClick={() => a.enabled && setSelectedAreaId(a.id)}
              disabled={!a.enabled}
              title={!a.enabled ? 'Próximamente' : ''}
              className={`am-focus shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-xl transition-all ${
                isActive
                  ? darkMode ? 'bg-slate-100 text-slate-900' : 'bg-slate-900 text-white'
                  : a.enabled
                    ? darkMode ? 'bg-slate-800/60 text-slate-300 hover:bg-slate-800' : 'bg-white text-slate-700 hover:bg-slate-100 border border-slate-200'
                    : darkMode ? 'bg-slate-800/30 text-slate-600 cursor-not-allowed opacity-50' : 'bg-slate-50 text-slate-400 cursor-not-allowed opacity-50 border border-slate-200'
              }`}
            >
              <span className="text-base">{a.icon}</span>
              <span className="am-sans text-sm font-semibold whitespace-nowrap">{a.label}</span>
              {!a.enabled && (
                <span className={`am-mono text-[9px] uppercase tracking-widest ${darkMode ? 'text-slate-600' : 'text-slate-400'}`}>
                  pronto
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );

  // Si área no está enabled, mostrar placeholder
  if (!selectedArea?.enabled) {
    return (
      <div>
        <AreaSelector />
        <div className={`${cardClass} am-noise rounded-3xl p-16 text-center`}>
          <div className="text-6xl mb-6">{selectedArea?.icon}</div>
          <h3 className={`am-display text-3xl font-medium mb-3 ${text}`}>
            {selectedAreaId} <span className="italic font-light">próximamente</span>
          </h3>
          <p className={`am-serif text-base ${subText} max-w-md mx-auto leading-relaxed`}>
            El análisis de la mente del examinador para esta área está en cola. Por ahora, podés explorar Infectología.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div>
        <AreaSelector />
        <div className={`${cardClass} rounded-2xl p-16 text-center am-noise`}>
          <motion.div
            animate={{ opacity: [0.3, 1, 0.3] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className={`am-mono text-sm tracking-wider ${muted}`}
          >
            ANALIZANDO MENTE DEL {selectedAreaId.toUpperCase()} · MYSQL
          </motion.div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <AreaSelector />
        <div className={`${cardClass} rounded-2xl p-10`}>
          <div className={`am-eyebrow mb-3 text-rose-500`}>ERROR</div>
          <p className={`am-serif text-2xl mb-2 ${text}`}>{error}</p>
        </div>
      </div>
    );
  }

  // ============================================================================
  // VIEW MACRO
  // ============================================================================
  if (view === 'macro') {
    const total = macroAnalysis?.total || 0;
    const phrases = macroAnalysis?.phrases || {};
    const topics = macroAnalysis?.topics || [];
    const phraseEntries = [
      { key: 'diagnostico', label: 'diagnóstico', count: phrases.diagnostico, color: PALETTE.purple.fill },
      { key: 'tratamiento', label: 'tratamiento', count: phrases.tratamiento, color: PALETTE.info.fill },
      { key: 'probable', label: 'más probable', count: phrases.probable, color: PALETTE.warning.fill },
      { key: 'agente', label: 'agente etiológico', count: phrases.agente, color: PALETTE.success.fill },
      { key: 'eleccion', label: 'de elección', count: phrases.eleccion, color: PALETTE.danger.fill },
    ];

    return (
      <div>
        <AreaSelector />
        <LevelStepper />
        <Breadcrumb />

        {/* HERO editorial */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="mb-14 grid lg:grid-cols-12 gap-8"
        >
          <div className="lg:col-span-7">
            <div className={`am-eyebrow mb-4 ${muted}`}>
              <Brain className="w-3 h-3 inline mr-2" />
              ÁREA · {total} PREGUNTAS · 16 AÑOS
            </div>
            <h2 className={`am-display text-5xl md:text-6xl font-medium mb-4 ${text}`}>
              Cómo piensa el <span className="italic font-light">{selectedArea?.specialist || 'examinador'}</span>
            </h2>
            <p className={`am-serif text-lg leading-relaxed ${subText} max-w-2xl`}>
              {areaData?.macro?.narrative || `Análisis cuantitativo de ${selectedAreaId} basado en MySQL.`}
            </p>
          </div>

          <div className="lg:col-span-5">
            <div className={`${cardClass} am-noise rounded-2xl p-6`}>
              <div className={`am-eyebrow mb-4 ${muted}`}>
                <Quote className="w-3 h-3 inline mr-2" />
                FRASE FAVORITA · TOP 5
              </div>
              <div className="space-y-2">
                {phraseEntries.map((p, i) => {
                  const pct = total > 0 ? ((p.count / total) * 100).toFixed(0) : 0;
                  return (
                    <motion.div
                      key={p.key}
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: 0.1 + i * 0.05 }}
                      className="flex items-center gap-3"
                    >
                      <span className={`am-numeric text-2xl tabular-nums w-12 text-right`} style={{ color: p.color }}>
                        {pct}
                      </span>
                      <span className={`am-mono text-[10px] ${muted}`}>%</span>
                      <span className={`am-serif italic text-sm flex-1 ${text}`}>
                        "{p.label}"
                      </span>
                      <span className={`am-mono text-[10px] tabular-nums ${muted}`}>
                        {p.count}
                      </span>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </div>
        </motion.section>

        {/* ============================================================== */}
        {/* LECTURA DEL EXPERTO — análisis crítico interpretativo          */}
        {/* ============================================================== */}
        {areaData?.macro?.expertAnalysis && (
          <ExpertAnalysisSection
            analysis={areaData.macro.expertAnalysis}
            darkMode={darkMode}
            dataAnalysis={dataAnalysis}
            areaName={selectedAreaId}
            totalQuestions={questions.length}
            onPredictionClick={(p) => {
              if (p.subtopic && p.topic) {
                setSelectedTopic(p.topic);
                setSelectedSubtopic(p.subtopic);
                setView('micro');
              } else if (p.topic) {
                goToMeso(p.topic);
              }
            }}
            onSubtopicClick={(topic, subtopic) => {
              setSelectedTopic(topic);
              setSelectedSubtopic(subtopic);
              setView('micro');
            }}
          />
        )}

        {/* PATRONES MENTALES */}
        <motion.section
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mb-12"
        >
          <div className="mb-6">
            <div className={`am-eyebrow mb-2 ${muted}`}>FIG. 01 · PATRONES MENTALES</div>
            <h3 className={`am-display text-3xl font-medium ${text}`}>
              Las 4 <span className="italic font-light">obsesiones</span> del examinador
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {(areaData?.macro?.patterns || []).map((p, i) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.04 * i }}
                className={`${cardClass} am-noise rounded-2xl p-5 relative overflow-hidden`}
              >
                <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: PALETTE.info.fill }} />
                <div className="flex items-baseline gap-3 mb-3">
                  <span className={`am-display text-5xl font-medium`} style={{ color: PALETTE.info.fill }}>
                    {p.id}
                  </span>
                  <div className={`am-numeric text-2xl ${text}`}>
                    {p.weight}<span className={`am-mono text-xs ${muted}`}>%</span>
                  </div>
                </div>
                <h4 className={`am-serif text-lg font-medium mb-2 ${text}`}>{p.name}</h4>
                <p className={`am-sans text-xs leading-relaxed ${subText}`}>{p.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* LAS 4 TRAMPAS DEL EXAMINADOR */}
        {areaData?.macro?.trapTypes && (
          <motion.section
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.25 }}
            className="mb-12"
          >
            <div className="mb-6">
              <div className={`am-eyebrow mb-2 text-amber-500`}>
                <AlertTriangle className="w-3 h-3 inline mr-2" />
                FIG. 02 · TRAMPAS RECURRENTES
              </div>
              <h3 className={`am-display text-3xl font-medium ${text}`}>
                Los 4 <span className="italic font-light">cuchillos</span> con que te corta
              </h3>
              <p className={`am-sans text-sm mt-2 ${subText}`}>
                Patrones de distractor que el examinador repite sistemáticamente
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {areaData.macro.trapTypes.map((t, i) => (
                <motion.div
                  key={t.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.04 * i }}
                  className={`${cardClass} am-noise rounded-2xl p-5 relative overflow-hidden`}
                  style={{
                    borderLeft: `3px solid ${PALETTE.warning.fill}`,
                  }}
                >
                  <div className="flex items-baseline gap-3 mb-3">
                    <span className={`am-mono text-[11px] font-bold px-2 py-0.5 rounded`} style={{ background: `${PALETTE.warning.fill}20`, color: PALETTE.warning.text }}>
                      TIPO {t.id}
                    </span>
                  </div>
                  <h4 className={`am-serif text-lg font-medium mb-2 ${text}`}>{t.name}</h4>
                  <div className={`p-3 rounded-lg ${innerBg} mt-2`}>
                    <div className={`am-eyebrow mb-1 ${muted}`}>EJEMPLO</div>
                    <p className={`am-serif italic text-sm ${text}`}>{t.example}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.section>
        )}

        {/* CASOS CLÍNICOS vs DIRECTAS */}
        <motion.section
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.28 }}
          className="mb-12 grid md:grid-cols-2 gap-4"
        >
          <div className={`${cardClass} am-noise rounded-2xl p-6`}>
            <div className={`am-eyebrow mb-3 ${muted}`}>FORMATO · CASOS CLÍNICOS</div>
            <div className="flex items-baseline gap-2 mb-3">
              <span className={`am-numeric-xl text-6xl text-indigo-500`}>
                {macroAnalysis?.casos || 0}
              </span>
              <span className={`am-mono text-sm ${muted}`}>preguntas</span>
            </div>
            <div className={`am-mono text-xs ${subText}`}>
              {macroAnalysis?.total ? Math.round((macroAnalysis.casos / macroAnalysis.total) * 100) : 0}% del total · con caso clínico extenso
            </div>
            <p className={`am-serif text-sm mt-3 leading-relaxed ${subText}`}>
              El examinador prefiere casos clínicos con killer feature patognomónico para forzar reconocimiento.
            </p>
          </div>
          <div className={`${cardClass} am-noise rounded-2xl p-6`}>
            <div className={`am-eyebrow mb-3 ${muted}`}>FORMATO · PREGUNTAS DIRECTAS</div>
            <div className="flex items-baseline gap-2 mb-3">
              <span className={`am-numeric-xl text-6xl text-emerald-500`}>
                {macroAnalysis?.directas || 0}
              </span>
              <span className={`am-mono text-sm ${muted}`}>preguntas</span>
            </div>
            <div className={`am-mono text-xs ${subText}`}>
              {macroAnalysis?.total ? Math.round((macroAnalysis.directas / macroAnalysis.total) * 100) : 0}% del total · sin caso, dato puro
            </div>
            <p className={`am-serif text-sm mt-3 leading-relaxed ${subText}`}>
              Reservadas para listas mnemotécnicas (NASA, NATA) o tratamientos de excepción.
            </p>
          </div>
        </motion.section>

        {/* GRID DE TEMAS con accuracy del usuario */}
        <motion.section
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <div className="mb-6">
            <div className={`am-eyebrow mb-2 ${muted}`}>FIG. 02 · TEMAS</div>
            <h3 className={`am-display text-3xl font-medium ${text}`}>
              Los <span className="italic font-light">{topics.length} territorios</span> de la mente infectológica
            </h3>
            <p className={`am-sans text-sm mt-2 ${subText}`}>
              Click sobre cualquier tema para drilldown · Color = tu accuracy en RM 2025
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {topics.map((t, i) => (
              <TopicMacroCard
                key={t.topic}
                topic={t}
                index={i}
                darkMode={darkMode}
                onClick={() => goToMeso(t.topic)}
              />
            ))}
          </div>
        </motion.section>
      </div>
    );
  }

  // ============================================================================
  // VIEW MESO
  // ============================================================================
  if (view === 'meso' && selectedTopic && mesoAnalysis) {
    const topicNarrative = areaData?.topics?.[selectedTopic];
    const userInTopic = userAnswers.filter(a => a.topic === selectedTopic);
    const userOk = userInTopic.filter(a => a.status === 'correct').length;
    const userAcc = userInTopic.length > 0 ? Math.round((userOk / userInTopic.length) * 100) : null;

    return (
      <div>
        <AreaSelector />
        <LevelStepper />
        <Breadcrumb />

        <motion.section
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className={`${cardClass} am-noise rounded-3xl p-8 mb-10`}
        >
          <div className={`am-eyebrow mb-3 ${muted}`}>
            <Layers className="w-3 h-3 inline mr-2" />
            TEMA · {mesoAnalysis.total} PREGUNTAS · 16 AÑOS
          </div>
          <h2 className={`am-display text-5xl font-medium mb-4 ${text}`}>{selectedTopic}</h2>

          {topicNarrative && (
            <p className={`am-serif text-lg leading-relaxed ${subText} max-w-3xl mb-4`}>
              {topicNarrative.narrative}
            </p>
          )}

          {userAcc !== null && (
            <div className={`flex items-baseline gap-3 mt-6 pt-6 border-t ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
              <span className={`am-eyebrow ${muted}`}>TU RM 2025</span>
              <span className={`am-numeric text-4xl`} style={{ color: accuracyColor(userAcc) }}>
                {userAcc}<span className={`am-mono text-sm ${muted} ml-1`}>%</span>
              </span>
              <span className={`am-mono text-xs ${subText}`}>
                {userOk}/{userInTopic.length} correctas
              </span>
            </div>
          )}
        </motion.section>

        {/* LECTURA DEL EXPERTO DEL TEMA — análisis crítico interpretativo */}
        {topicNarrative?.expertAnalysis && (
          <ThemeExpertSection
            analysis={topicNarrative.expertAnalysis}
            topicName={selectedTopic}
            darkMode={darkMode}
            tierMeta={{
              MUY_ALTA: { label: 'MUY ALTA', color: PALETTE.danger.fill, bg: 'rgba(244, 63, 94, 0.08)' },
              ALTA: { label: 'ALTA', color: PALETTE.warning.fill, bg: 'rgba(245, 158, 11, 0.08)' },
              MEDIA: { label: 'MEDIA', color: PALETTE.info.fill, bg: 'rgba(99, 102, 241, 0.08)' },
              BAJA: { label: 'BAJA', color: PALETTE.success.fill, bg: 'rgba(16, 185, 129, 0.08)' },
            }}
          />
        )}

        {/* TIMELINE temporal */}
        <motion.section
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className={`${cardClass} rounded-3xl p-8 mb-10`}
        >
          <div className="mb-6">
            <div className={`am-eyebrow mb-2 ${muted}`}>FIG. 01 · LÍNEA TEMPORAL</div>
            <h3 className={`am-display text-2xl font-medium ${text}`}>
              Cuándo <span className="italic font-light">cae</span> {selectedTopic}
            </h3>
          </div>

          <div style={{ width: '100%', height: 200 }}>
            <ResponsiveContainer>
              <BarChart data={mesoAnalysis.timeline} margin={{ top: 20, right: 0, bottom: 30, left: 0 }}>
                <CartesianGrid strokeDasharray="2 4" stroke={darkMode ? 'rgba(148,163,184,0.1)' : 'rgba(15,23,42,0.06)'} vertical={false} />
                <XAxis dataKey="year" stroke={darkMode ? '#64748b' : '#94a3b8'} tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis stroke={darkMode ? '#64748b' : '#94a3b8'} tick={{ fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip
                  cursor={{ fill: darkMode ? 'rgba(99,102,241,0.05)' : 'rgba(99,102,241,0.04)' }}
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const d = payload[0].payload;
                    return (
                      <div className={`${cardClass} rounded-xl p-3`}>
                        <div className={`am-eyebrow ${muted}`}>RM {d.year}</div>
                        <div className={`am-numeric text-2xl ${text}`}>{d.count}</div>
                        <div className={`am-mono text-[10px] ${subText}`}>preguntas</div>
                      </div>
                    );
                  }}
                />
                <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                  {mesoAnalysis.timeline.map((d, i) => (
                    <Cell key={i} fill={d.count === 0 ? (darkMode ? '#334155' : '#e2e8f0') : PALETTE.info.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.section>

        {/* PREDICCIONES 2026 (de JSON narrativo) */}
        {topicNarrative?.prediction2026 && (
          <motion.section
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.15 }}
            className={`${cardClass} am-noise rounded-3xl p-8 mb-10 relative overflow-hidden`}
          >
            <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: PALETTE.danger.fill }} />
            <div className={`am-eyebrow mb-3 ${muted}`}>
              <Crosshair className="w-3 h-3 inline mr-2" />
              PREDICCIÓN RM 2026
            </div>
            <p className={`am-serif text-xl leading-relaxed ${text}`}>
              {topicNarrative.prediction2026}
            </p>
          </motion.section>
        )}

        {/* SUBTEMAS con gap + tu accuracy */}
        <motion.section
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
        >
          <div className="mb-6">
            <div className={`am-eyebrow mb-2 ${muted}`}>SUBTEMAS · ORDENADOS POR GAP</div>
            <h3 className={`am-display text-2xl font-medium ${text}`}>
              Subtemas <span className="italic font-light">priorizables</span>
            </h3>
            <p className={`am-sans text-sm mt-2 ${subText}`}>
              Gap más alto = más probable que caiga en RM 2026
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {mesoAnalysis.subtopics.slice(0, 12).map((s, i) => (
              <SubtopicMesoCard
                key={s.subtopic}
                sub={s}
                index={i}
                darkMode={darkMode}
                onClick={() => goToMicro(s.subtopic)}
              />
            ))}
          </div>
        </motion.section>
      </div>
    );
  }

  // ============================================================================
  // VIEW MICRO
  // ============================================================================
  if (view === 'micro' && selectedSubtopic && microAnalysis) {
    const microNarrative = areaData?.subtopics?.[selectedSubtopic];
    const subQs = microAnalysis.subQs;

    return (
      <div>
        <AreaSelector />
        <LevelStepper />
        <Breadcrumb />

        <motion.section
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className={`${cardClass} am-noise rounded-3xl p-8 mb-10`}
        >
          <div className={`am-eyebrow mb-3 ${muted}`}>
            <Target className="w-3 h-3 inline mr-2" />
            SUBTEMA · {subQs.length} PREGUNTA{subQs.length !== 1 ? 'S' : ''} HISTÓRICA{subQs.length !== 1 ? 'S' : ''}
          </div>
          <h2 className={`am-display text-5xl font-medium mb-4 ${text}`}>{selectedSubtopic}</h2>
          {microNarrative?.narrative && (
            <p className={`am-serif text-lg leading-relaxed ${subText} max-w-3xl`}>
              {microNarrative.narrative}
            </p>
          )}
        </motion.section>

        {/* PREDICCIÓN 2026 destacada */}
        {microNarrative?.prediction2026 && (
          <motion.section
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="mb-10"
          >
            <div className={`${cardClass} am-noise rounded-3xl p-8 relative overflow-hidden`}>
              <div
                className="absolute top-0 left-0 right-0 h-1"
                style={{
                  background: probColor(microNarrative.prediction2026.probability),
                  boxShadow: `0 0 20px ${probColor(microNarrative.prediction2026.probability)}80`,
                }}
              />

              <div className="grid lg:grid-cols-2 gap-8 items-start">
                <div>
                  <div className={`am-eyebrow mb-3 ${muted}`}>
                    <Crosshair className="w-3 h-3 inline mr-2" />
                    PREDICCIÓN RM 2026
                  </div>
                  <div className="flex items-baseline gap-2 mb-3">
                    <span
                      className={`am-numeric-xl text-8xl`}
                      style={{ color: probColor(microNarrative.prediction2026.probability) }}
                    >
                      {microNarrative.prediction2026.probability}
                    </span>
                    <span className={`am-mono text-2xl ${muted}`}>%</span>
                  </div>
                  <div className={`am-eyebrow ${muted}`}>PROBABILIDAD DE CAÍDA</div>
                </div>

                <div className="space-y-4">
                  <div>
                    <div className={`am-eyebrow mb-1 ${muted}`}>FORMATO ESPERADO</div>
                    <div className={`am-serif text-base ${text}`}>{microNarrative.prediction2026.format}</div>
                  </div>
                  {microNarrative.prediction2026.expectedTrap && (
                    <div>
                      <div className={`am-eyebrow mb-1 text-amber-500`}>
                        <AlertTriangle className="w-3 h-3 inline mr-1" />
                        TRAMPA ESPERADA
                      </div>
                      <div className={`am-serif italic text-sm ${text}`}>
                        "{microNarrative.prediction2026.expectedTrap}"
                      </div>
                    </div>
                  )}
                  {microNarrative.prediction2026.killerToRemember && (
                    <div className={`p-3 rounded-xl ${darkMode ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-emerald-50 border border-emerald-200'}`}>
                      <div className={`am-eyebrow mb-1 text-emerald-500`}>
                        <Sparkles className="w-3 h-3 inline mr-1" />
                        KILLER A RECORDAR
                      </div>
                      <div className={`am-serif text-base font-medium ${text}`}>
                        {microNarrative.prediction2026.killerToRemember}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </motion.section>
        )}

        {/* PREGUNTAS HISTÓRICAS */}
        {microNarrative?.history && microNarrative.history.length > 0 && (
          <motion.section
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.15 }}
            className="mb-10"
          >
            <div className="mb-6">
              <div className={`am-eyebrow mb-2 ${muted}`}>HISTÓRICO · CÓMO SE PREGUNTÓ</div>
              <h3 className={`am-display text-2xl font-medium ${text}`}>
                Las preguntas <span className="italic font-light">reales</span>
              </h3>
            </div>
            <div className="space-y-4">
              {microNarrative.history.map((h, i) => (
                <div key={i} className={`${cardClass} rounded-2xl p-6`}>
                  <div className="flex items-baseline gap-3 mb-3 flex-wrap">
                    <span className={`am-numeric text-2xl ${text}`}>{h.year}</span>
                    <span className={`am-eyebrow px-2 py-0.5 rounded-full ${darkMode ? 'bg-slate-800' : 'bg-slate-100'} ${muted}`}>
                      FORMATO: {h.format}
                    </span>
                    <span className={`am-eyebrow px-2 py-0.5 rounded-full bg-indigo-500/15 text-indigo-500`}>
                      PATRÓN {h.pattern}
                    </span>
                  </div>
                  <p className={`am-serif text-base leading-relaxed mb-4 ${text}`}>
                    {h.question}
                  </p>
                  <div className={`flex items-baseline gap-2 mb-3`}>
                    <span className={`am-eyebrow text-emerald-500`}>CORRECTA</span>
                    <span className={`am-serif text-base font-semibold text-emerald-500`}>
                      {h.correct}
                    </span>
                  </div>
                  {h.distractors && (
                    <div className={`am-mono text-xs ${subText}`}>
                      Distractores: {h.distractors.join(' · ')}
                    </div>
                  )}
                  {h.trap && (
                    <div className={`mt-3 pt-3 border-t ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                      <span className={`am-eyebrow text-amber-500`}>TRAMPA</span>
                      <p className={`am-serif italic text-sm mt-1 ${text}`}>{h.trap}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </motion.section>
        )}

        {/* POOL DE DISTRACTORES (calculado en vivo desde MySQL) */}
        {microAnalysis.distractors.length > 0 && (
          <motion.section
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
            className={`${cardClass} rounded-3xl p-8`}
          >
            <div className="mb-6">
              <div className={`am-eyebrow mb-2 ${muted}`}>POOL DE DISTRACTORES · MYSQL EN VIVO</div>
              <h3 className={`am-display text-2xl font-medium ${text}`}>
                Lo que <span className="italic font-light">rota</span> el examinador
              </h3>
              <p className={`am-sans text-sm mt-2 ${subText}`}>
                Distractores extraídos de las preguntas históricas. Los que repiten = pool fijo.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {microAnalysis.distractors.slice(0, 12).map((d, i) => (
                <div
                  key={i}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl ${innerBg}`}
                >
                  <span className={`am-numeric text-xl ${d.count > 1 ? 'text-rose-500' : muted}`}>
                    ×{d.count}
                  </span>
                  <span className={`am-serif text-sm flex-1 truncate ${text}`}>{d.text}</span>
                  <span className={`am-mono text-[10px] ${muted}`}>
                    {d.years.join(',').replace(/RM /g, '')}
                  </span>
                </div>
              ))}
            </div>
          </motion.section>
        )}

        {/* Fallback si no hay narrativa específica del subtema */}
        {!microNarrative && (
          <div className={`${cardClass} rounded-2xl p-8 text-center`}>
            <p className={`am-serif italic text-lg ${subText}`}>
              Análisis específico de este subtema pendiente. Mientras tanto, ya ves las preguntas históricas + pool de distractores arriba.
            </p>
          </div>
        )}
      </div>
    );
  }
  return null;
}

// ============================================================================
// SqlBlock — bloque de query SQL con copy
// ============================================================================
function SqlBlock({ title, query, darkMode }) {
  const [copied, setCopied] = useState(false);
  const innerBg = darkMode ? 'bg-slate-950/80' : 'bg-slate-900';
  const text = darkMode ? 'text-emerald-300' : 'text-emerald-300';
  const muted = darkMode ? 'text-slate-500' : 'text-slate-400';

  const copy = () => {
    navigator.clipboard?.writeText(query);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className={`rounded-xl overflow-hidden border ${darkMode ? 'border-slate-700' : 'border-slate-300'}`}>
      <div className={`flex items-center justify-between px-4 py-2 ${darkMode ? 'bg-slate-800' : 'bg-slate-100'}`}>
        <span className={`am-mono text-xs uppercase tracking-wider ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>{title}</span>
        <button
          onClick={copy}
          className={`am-mono text-[10px] uppercase tracking-wider px-2 py-1 rounded ${darkMode ? 'text-slate-400 hover:bg-slate-700' : 'text-slate-500 hover:bg-slate-200'}`}
        >
          {copied ? '✓ copiado' : 'copiar'}
        </button>
      </div>
      <pre className={`am-mono text-xs p-4 ${innerBg} ${text} overflow-x-auto leading-relaxed`}>
        {query}
      </pre>
    </div>
  );
}

// ============================================================================
// TopicMacroCard — card por tema en MACRO
// ============================================================================
// ============================================================================
// ExpertAnalysisSection — la "Lectura del Experto" interactiva y rica
// ============================================================================
function ExpertAnalysisSection({ analysis, darkMode, onPredictionClick, dataAnalysis, areaName, totalQuestions, onSubtopicClick }) {
  const [activeTab, setActiveTab] = useState('insights');
  const [expandedInsight, setExpandedInsight] = useState(null);
  const [expandedTrap, setExpandedTrap] = useState(null);

  const cardClass = darkMode ? 'am-card-dark' : 'am-card-light';
  const text = darkMode ? 'text-slate-100' : 'text-slate-900';
  const subText = darkMode ? 'text-slate-400' : 'text-slate-500';
  const muted = darkMode ? 'text-slate-500' : 'text-slate-400';
  const innerBg = darkMode ? 'bg-slate-900/40' : 'bg-slate-50/80';

  const tierMeta = {
    MUY_ALTA: { label: 'MUY ALTA', color: PALETTE.danger.fill, bg: 'rgba(244, 63, 94, 0.08)' },
    ALTA: { label: 'ALTA', color: PALETTE.warning.fill, bg: 'rgba(245, 158, 11, 0.08)' },
    MEDIA: { label: 'MEDIA', color: PALETTE.info.fill, bg: 'rgba(99, 102, 241, 0.08)' },
    BAJA: { label: 'BAJA', color: PALETTE.success.fill, bg: 'rgba(16, 185, 129, 0.08)' },
  };

  const subTabs = [
    { id: 'insights', label: 'Insights críticos', icon: Sparkles, count: analysis.criticalInsights?.length || 0 },
    { id: 'predictions', label: 'Predicciones 2026', icon: Crosshair, count: analysis.predictions2026?.length || 0 },
    { id: 'traps', label: 'Trampas del experto', icon: AlertTriangle, count: analysis.expertTraps?.length || 0 },
    { id: 'data', label: 'Data + análisis', icon: Layers, count: 6 },
  ];

  return (
    <motion.section
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.1 }}
      className={`${cardClass} am-noise rounded-3xl mb-12 overflow-hidden relative`}
    >
      {/* Top accent bar — púrpura experto */}
      <div
        className="absolute top-0 left-0 right-0 h-[2px]"
        style={{
          background: `linear-gradient(90deg, transparent 0%, ${PALETTE.purple.fill} 50%, transparent 100%)`,
          boxShadow: `0 0 16px ${PALETTE.purple.fill}80`,
        }}
      />

      {/* Header editorial */}
      <div className="p-8 pb-4">
        <div className={`am-eyebrow mb-3`} style={{ color: PALETTE.purple.fill }}>
          <Brain className="w-3 h-3 inline mr-2" />
          LECTURA DEL EXPERTO · INTERPRETACIÓN CLÍNICA
        </div>
        <h3 className={`am-display text-4xl font-medium ${text} mb-2 leading-tight`}>
          {analysis.subtitle}
        </h3>
        <p className={`am-serif text-base leading-relaxed mt-4 ${subText} max-w-4xl`}>
          {analysis.personalityNarrative}
        </p>
      </div>

      {/* Las 4 obsesiones */}
      {analysis.obsessions && (
        <div className="px-8 pb-6">
          <div className={`am-eyebrow mb-4 ${muted}`}>SUS 4 OBSESIONES</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {analysis.obsessions.map((o, i) => (
              <motion.div
                key={o.title}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.05 * i }}
                className={`p-4 rounded-xl ${innerBg}`}
                style={{ borderLeft: `2px solid ${PALETTE.purple.fill}` }}
              >
                <div className={`am-mono text-[10px] font-bold mb-2`} style={{ color: PALETTE.purple.text }}>
                  #{String(i + 1).padStart(2, '0')}
                </div>
                <h4 className={`am-serif text-sm font-semibold mb-1 ${text}`}>{o.title}</h4>
                <p className={`am-sans text-xs leading-snug ${subText}`}>{o.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Hairline divider */}
      <div className={`mx-8 ${darkMode ? 'am-hairline-dark' : 'am-hairline-light'}`} />

      {/* Sub-tabs interactivas */}
      <div className="px-8 pt-6 pb-2">
        <div className="flex flex-wrap gap-2">
          {subTabs.map(t => {
            const Icon = t.icon;
            const isActive = activeTab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={`am-focus flex items-center gap-2 px-4 py-2 rounded-xl transition-all ${
                  isActive
                    ? darkMode ? 'bg-slate-100 text-slate-900' : 'bg-slate-900 text-white'
                    : darkMode ? 'bg-slate-800/50 text-slate-300 hover:bg-slate-800' : 'bg-white text-slate-700 hover:bg-slate-100 border border-slate-200'
                }`}
              >
                <Icon className="w-4 h-4" strokeWidth={1.75} />
                <span className="am-sans text-sm font-semibold">{t.label}</span>
                <span className={`am-mono text-[10px] px-1.5 py-0.5 rounded ${
                  isActive ? (darkMode ? 'bg-slate-300 text-slate-700' : 'bg-slate-700 text-slate-300') : (darkMode ? 'bg-slate-700 text-slate-400' : 'bg-slate-100 text-slate-500')
                }`}>
                  {t.count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* CONTENT — switch por tab */}
      <div className="p-8 pt-4">

        {/* TAB: INSIGHTS CRÍTICOS */}
        {activeTab === 'insights' && (
          <AnimatePresence mode="wait">
            <motion.div
              key="insights"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="space-y-3"
            >
              {(analysis.criticalInsights || []).map((insight, i) => {
                const isExpanded = expandedInsight === insight.id;
                return (
                  <motion.div
                    key={insight.id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.04 * i }}
                    className={`${innerBg} rounded-2xl overflow-hidden border ${darkMode ? 'border-slate-700/50' : 'border-slate-200'}`}
                  >
                    <button
                      onClick={() => setExpandedInsight(isExpanded ? null : insight.id)}
                      className="am-focus w-full text-left p-5 flex items-start gap-4 hover:bg-black/5 transition-colors"
                    >
                      <div className="text-3xl flex-shrink-0">{insight.icon}</div>
                      <div className="flex-1 min-w-0">
                        <div className={`am-mono text-[10px] uppercase tracking-widest mb-1 ${muted}`}>
                          INSIGHT {String(i + 1).padStart(2, '0')}
                        </div>
                        <h4 className={`am-serif text-xl font-medium ${text}`}>
                          {insight.title}
                        </h4>
                      </div>
                      <ChevronRight className={`w-5 h-5 ${muted} flex-shrink-0 mt-1 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                    </button>

                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease: [0.2, 0.8, 0.2, 1] }}
                          className="overflow-hidden"
                        >
                          <div className={`px-5 pb-5 pl-[68px]`}>
                            <p className={`am-serif text-base leading-relaxed mb-4 ${text}`}>
                              {insight.narrative}
                            </p>
                            <div className="grid md:grid-cols-2 gap-3">
                              <div className={`p-3 rounded-xl ${darkMode ? 'bg-slate-800/60' : 'bg-white border border-slate-200'}`}>
                                <div className={`am-eyebrow mb-1 ${muted}`}>EVIDENCIA</div>
                                <p className={`am-sans text-sm ${text}`}>{insight.evidence}</p>
                              </div>
                              <div
                                className={`p-3 rounded-xl border`}
                                style={{
                                  background: darkMode ? `${PALETTE.purple.fill}10` : `${PALETTE.purple.fill}08`,
                                  borderColor: `${PALETTE.purple.fill}30`,
                                }}
                              >
                                <div className={`am-eyebrow mb-1`} style={{ color: PALETTE.purple.text }}>IMPLICACIÓN</div>
                                <p className={`am-sans text-sm font-medium ${text}`}>{insight.implication}</p>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </motion.div>
          </AnimatePresence>
        )}

        {/* TAB: PREDICCIONES 2026 — workspace estratégico master-detail */}
        {activeTab === 'predictions' && (
          <AnimatePresence mode="wait">
            <motion.div
              key="predictions"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <StrategicWorkspace
                predictions={analysis.predictions2026 || []}
                tierMeta={tierMeta}
                darkMode={darkMode}
                onPredictionClick={onPredictionClick}
                areaName={areaName}
                dataAnalysis={dataAnalysis}
              />
            </motion.div>
          </AnimatePresence>
        )}

        {/* TAB: TRAMPAS DEL EXPERTO */}
        {activeTab === 'traps' && (
          <AnimatePresence mode="wait">
            <motion.div
              key="traps"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="grid md:grid-cols-2 gap-3"
            >
              {(analysis.expertTraps || []).map((trap, i) => {
                const isExpanded = expandedTrap === trap.id;
                return (
                  <motion.div
                    key={trap.id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.04 * i }}
                    className={`${innerBg} rounded-2xl overflow-hidden border ${darkMode ? 'border-amber-500/20' : 'border-amber-200'}`}
                  >
                    <button
                      onClick={() => setExpandedTrap(isExpanded ? null : trap.id)}
                      className="am-focus w-full text-left p-5 hover:bg-black/5 transition-colors"
                    >
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <div className={`am-eyebrow mb-1 text-amber-500`}>{trap.category}</div>
                          <h4 className={`am-serif text-lg font-semibold ${text}`}>{trap.pair}</h4>
                          <p className={`am-sans text-xs mt-1 ${subText}`}>{trap.frequency}</p>
                        </div>
                        <ChevronRight className={`w-4 h-4 ${muted} flex-shrink-0 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                      </div>
                    </button>

                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease: [0.2, 0.8, 0.2, 1] }}
                          className="overflow-hidden"
                        >
                          <div className="px-5 pb-5 space-y-3">
                            <div>
                              <div className={`am-eyebrow mb-1 ${muted}`}>POR QUÉ ENGAÑA</div>
                              <p className={`am-serif text-sm ${text} leading-relaxed`}>{trap.explanation}</p>
                            </div>
                            <div
                              className="p-3 rounded-xl border"
                              style={{
                                background: darkMode ? 'rgba(16,185,129,0.10)' : 'rgba(16,185,129,0.06)',
                                borderColor: 'rgba(16,185,129,0.30)',
                              }}
                            >
                              <div className={`am-eyebrow mb-1 text-emerald-500`}>KILLER QUE LO RESUELVE</div>
                              <p className={`am-serif text-sm font-medium ${text} leading-relaxed`}>{trap.killer}</p>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </motion.div>
          </AnimatePresence>
        )}

        {/* TAB: DATA + ANÁLISIS ESTRATÉGICO */}
        {activeTab === 'data' && dataAnalysis && (
          <AnimatePresence mode="wait">
            <motion.div
              key="data"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="space-y-6"
            >
              <DataAndAnalysisSection
                data={dataAnalysis}
                darkMode={darkMode}
                areaName={areaName}
                totalQuestions={totalQuestions}
                onSubtopicClick={onSubtopicClick}
              />
            </motion.div>
          </AnimatePresence>
        )}

      </div>
    </motion.section>
  );
}

// ============================================================================
// ThemeExpertSection — Lectura del Experto a nivel TEMA (MESO)
// Misma estructura que ExpertAnalysisSection pero scope tema
// ============================================================================
function ThemeExpertSection({ analysis, topicName, darkMode, tierMeta }) {
  const [activeTab, setActiveTab] = useState('subtopics');
  const [expandedSub, setExpandedSub] = useState(null);
  const [expandedTrap, setExpandedTrap] = useState(null);

  const cardClass = darkMode ? 'am-card-dark' : 'am-card-light';
  const text = darkMode ? 'text-slate-100' : 'text-slate-900';
  const subText = darkMode ? 'text-slate-400' : 'text-slate-500';
  const muted = darkMode ? 'text-slate-500' : 'text-slate-400';
  const innerBg = darkMode ? 'bg-slate-900/40' : 'bg-slate-50/80';

  const subTabs = [
    { id: 'subtopics', label: 'Subtopics analizados', icon: Brain, count: analysis.subtopicsAnalysis?.length || 0 },
    { id: 'predictions', label: 'Predicciones del tema', icon: Crosshair, count: analysis.predictions2026?.length || 0 },
    { id: 'traps', label: 'Trampas del tema', icon: AlertTriangle, count: analysis.traps?.length || 0 },
    { id: 'study', label: 'Insights de estudio', icon: Sparkles, count: 4 },
  ];

  return (
    <motion.section
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.1 }}
      className={`${cardClass} am-noise rounded-3xl mb-10 overflow-hidden relative`}
    >
      <div
        className="absolute top-0 left-0 right-0 h-[2px]"
        style={{
          background: `linear-gradient(90deg, transparent 0%, ${PALETTE.purple.fill} 50%, transparent 100%)`,
          boxShadow: `0 0 16px ${PALETTE.purple.fill}80`,
        }}
      />

      {/* Header del análisis */}
      <div className="p-8 pb-4">
        <div className={`am-eyebrow mb-3`} style={{ color: PALETTE.purple.fill }}>
          <Brain className="w-3 h-3 inline mr-2" />
          LECTURA DEL EXPERTO · {topicName.toUpperCase()}
        </div>
        <h3 className={`am-display text-3xl font-medium ${text} mb-3 leading-tight`}>
          {analysis.subtitle}
        </h3>
        <p className={`am-serif text-base leading-relaxed mt-2 ${subText} max-w-4xl`}>
          {analysis.personalityNarrative}
        </p>
      </div>

      {/* Insight estructural — 3 anillos concéntricos */}
      {analysis.structuralInsight && (
        <div className="px-8 pb-6">
          <div className={`am-eyebrow mb-4 ${muted}`}>ESTRUCTURA DEL TEMA</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {analysis.structuralInsight.map((ring, i) => {
              const ringColor = i === 0 ? PALETTE.danger.fill : i === 1 ? PALETTE.warning.fill : PALETTE.info.fill;
              return (
                <motion.div
                  key={ring.ring}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.05 * i }}
                  className={`p-4 rounded-xl ${innerBg}`}
                  style={{ borderLeft: `3px solid ${ringColor}` }}
                >
                  <div className={`am-eyebrow text-[10px] mb-2`} style={{ color: ringColor }}>
                    {ring.ring}
                  </div>
                  <h4 className={`am-serif text-sm font-semibold mb-1 ${text}`}>{ring.members}</h4>
                  <p className={`am-sans text-xs italic ${subText}`}>{ring.meaning}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      )}

      <div className={`mx-8 ${darkMode ? 'am-hairline-dark' : 'am-hairline-light'}`} />

      {/* Sub-tabs */}
      <div className="px-8 pt-6 pb-2">
        <div className="flex flex-wrap gap-2">
          {subTabs.map(t => {
            const Icon = t.icon;
            const isActive = activeTab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={`am-focus flex items-center gap-2 px-4 py-2 rounded-xl transition-all ${
                  isActive
                    ? darkMode ? 'bg-slate-100 text-slate-900' : 'bg-slate-900 text-white'
                    : darkMode ? 'bg-slate-800/50 text-slate-300 hover:bg-slate-800' : 'bg-white text-slate-700 hover:bg-slate-100 border border-slate-200'
                }`}
              >
                <Icon className="w-4 h-4" strokeWidth={1.75} />
                <span className="am-sans text-sm font-semibold">{t.label}</span>
                <span className={`am-mono text-[10px] px-1.5 py-0.5 rounded ${
                  isActive ? (darkMode ? 'bg-slate-300 text-slate-700' : 'bg-slate-700 text-slate-300') : (darkMode ? 'bg-slate-700 text-slate-400' : 'bg-slate-100 text-slate-500')
                }`}>
                  {t.count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="p-8 pt-4">

        {/* TAB: SUBTOPICS ANALIZADOS */}
        {activeTab === 'subtopics' && (
          <AnimatePresence mode="wait">
            <motion.div
              key="subtopics"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="space-y-3"
            >
              {(analysis.subtopicsAnalysis || []).map((sub, i) => {
                const isExpanded = expandedSub === sub.id;
                const tier = tierMeta[sub.tier] || tierMeta.MEDIA;
                return (
                  <motion.div
                    key={sub.id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.04 * i }}
                    className={`${innerBg} rounded-2xl overflow-hidden border ${darkMode ? 'border-slate-700/50' : 'border-slate-200'}`}
                    style={{ borderLeft: `3px solid ${tier.color}` }}
                  >
                    <button
                      onClick={() => setExpandedSub(isExpanded ? null : sub.id)}
                      className="am-focus w-full text-left p-5 flex items-start gap-4 hover:bg-black/5 transition-colors"
                    >
                      <div className="text-3xl flex-shrink-0">{sub.icon}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span
                            className="am-eyebrow text-[9px] px-2 py-0.5 rounded-full"
                            style={{ background: tier.bg, color: tier.color, border: `1px solid ${tier.color}30` }}
                          >
                            {tier.label}
                          </span>
                          <span className={`am-mono text-[10px] ${muted}`}>
                            {sub.stats?.count} preg · {sub.stats?.frequency} · gap {sub.stats?.gap}a
                          </span>
                        </div>
                        <h4 className={`am-serif text-xl font-medium ${text}`}>
                          {sub.name}
                        </h4>
                        <p className={`am-serif italic text-sm mt-0.5 ${subText}`}>
                          {sub.tagline}
                        </p>
                      </div>
                      <div className="flex flex-col items-center flex-shrink-0">
                        <div className={`am-numeric text-3xl`} style={{ color: tier.color }}>
                          {sub.probability}
                        </div>
                        <div className={`am-mono text-[9px] ${muted}`}>% 2026</div>
                      </div>
                      <ChevronRight className={`w-5 h-5 ${muted} flex-shrink-0 mt-1 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                    </button>

                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease: [0.2, 0.8, 0.2, 1] }}
                          className="overflow-hidden"
                        >
                          <div className={`px-5 pb-5 pl-[68px] space-y-4`}>
                            <div>
                              <div className={`am-eyebrow mb-1 ${muted}`}>HISTORIA</div>
                              <p className={`am-serif text-sm leading-relaxed ${text}`}>{sub.history}</p>
                            </div>

                            <div>
                              <div className={`am-eyebrow mb-1 ${muted}`}>PATRÓN OBSERVADO</div>
                              <p className={`am-serif text-sm leading-relaxed ${text}`}>{sub.pattern}</p>
                            </div>

                            {sub.gapsCovered && sub.gapsCovered.length > 0 && (
                              <div>
                                <div className={`am-eyebrow mb-2 ${muted}`}>GAPS · ANGLES YA PREGUNTADOS</div>
                                <div className="space-y-1">
                                  {sub.gapsCovered.map((g, gi) => (
                                    <div
                                      key={gi}
                                      className={`flex items-center gap-3 px-3 py-2 rounded-lg text-xs ${darkMode ? 'bg-slate-800/50' : 'bg-white border border-slate-200'}`}
                                      style={g.alert ? { borderLeft: `2px solid ${PALETTE.danger.fill}` } : {}}
                                    >
                                      <span className={`am-serif flex-1 truncate ${text}`}>{g.topic}</span>
                                      <span className={`am-mono text-[10px] ${muted}`}>{g.year}</span>
                                      <span className={`am-numeric text-base font-bold`} style={{ color: g.gap >= 13 ? PALETTE.danger.fill : g.gap >= 8 ? PALETTE.warning.fill : g.gap >= 5 ? PALETTE.info.fill : muted }}>
                                        gap {g.gap}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            <div
                              className={`p-3 rounded-xl border`}
                              style={{
                                background: darkMode ? `${PALETTE.purple.fill}10` : `${PALETTE.purple.fill}08`,
                                borderColor: `${PALETTE.purple.fill}30`,
                              }}
                            >
                              <div className={`am-eyebrow mb-1`} style={{ color: PALETTE.purple.text }}>LECTURA CRÍTICA</div>
                              <p className={`am-serif text-sm font-medium leading-relaxed ${text}`}>{sub.criticalReading}</p>
                            </div>

                            {sub.topPredictions2026 && (
                              <div
                                className="p-3 rounded-xl border"
                                style={{
                                  background: darkMode ? 'rgba(16,185,129,0.10)' : 'rgba(16,185,129,0.06)',
                                  borderColor: 'rgba(16,185,129,0.30)',
                                }}
                              >
                                <div className={`am-eyebrow mb-2 text-emerald-500`}>TOP PREDICCIONES 2026</div>
                                <ul className={`am-serif text-sm space-y-1.5 ${text}`}>
                                  {sub.topPredictions2026.map((p, pi) => (
                                    <li key={pi}>• {p}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </motion.div>
          </AnimatePresence>
        )}

        {/* TAB: PREDICCIONES DEL TEMA */}
        {activeTab === 'predictions' && (
          <AnimatePresence mode="wait">
            <motion.div
              key="predictions"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="space-y-2.5"
            >
              {(analysis.predictions2026 || []).map((p, i) => {
                const tier = tierMeta[p.tier] || tierMeta.MEDIA;
                return (
                  <motion.div
                    key={p.rank}
                    initial={{ opacity: 0, x: -6 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: 0.04 * i }}
                    className={`${innerBg} rounded-xl p-4`}
                    style={{ borderLeft: `2px solid ${tier.color}` }}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex flex-col items-center flex-shrink-0 w-12">
                        <div className={`am-mono text-[10px] font-bold ${muted}`}>#{String(p.rank).padStart(2, '0')}</div>
                        <div className={`am-numeric text-2xl mt-0.5`} style={{ color: tier.color }}>{p.probability}</div>
                        <div className={`am-mono text-[8px] ${muted}`}>%</div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span
                            className="am-eyebrow text-[8px] px-1.5 py-0.5 rounded-full"
                            style={{ background: tier.bg, color: tier.color, border: `1px solid ${tier.color}30` }}
                          >
                            {tier.label}
                          </span>
                        </div>
                        <h5 className={`am-serif text-base font-medium ${text}`}>{p.subtopic}</h5>
                        <p className={`am-sans text-xs mt-1 leading-relaxed ${subText}`}>
                          <strong className={text}>Sustento: </strong>{p.reasoning}
                        </p>
                        {p.killer && (
                          <div
                            className="mt-2 px-2.5 py-1.5 rounded-md text-xs leading-snug"
                            style={{
                              background: darkMode ? 'rgba(16,185,129,0.10)' : 'rgba(16,185,129,0.06)',
                              border: '1px solid rgba(16,185,129,0.30)',
                            }}
                          >
                            <span className={`am-eyebrow text-[8px] mr-1.5 text-emerald-500`}>KILLER:</span>
                            <span className={`am-serif ${text}`}>{p.killer}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          </AnimatePresence>
        )}

        {/* TAB: TRAMPAS DEL TEMA */}
        {activeTab === 'traps' && (
          <AnimatePresence mode="wait">
            <motion.div
              key="traps"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="grid md:grid-cols-2 gap-3"
            >
              {(analysis.traps || []).map((trap, i) => {
                const isExpanded = expandedTrap === trap.id;
                return (
                  <motion.div
                    key={trap.id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.04 * i }}
                    className={`${innerBg} rounded-2xl overflow-hidden border ${darkMode ? 'border-amber-500/20' : 'border-amber-200'}`}
                  >
                    <button
                      onClick={() => setExpandedTrap(isExpanded ? null : trap.id)}
                      className="am-focus w-full text-left p-5 hover:bg-black/5 transition-colors"
                    >
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <h4 className={`am-serif text-lg font-semibold ${text}`}>{trap.name}</h4>
                        </div>
                        <ChevronRight className={`w-4 h-4 ${muted} flex-shrink-0 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                      </div>
                    </button>

                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease: [0.2, 0.8, 0.2, 1] }}
                          className="overflow-hidden"
                        >
                          <div className="px-5 pb-5 space-y-3">
                            <div>
                              <div className={`am-eyebrow mb-1 ${muted}`}>POR QUÉ ENGAÑA</div>
                              <p className={`am-serif text-sm ${text} leading-relaxed`}>{trap.explanation}</p>
                            </div>
                            <div
                              className="p-3 rounded-xl border"
                              style={{
                                background: darkMode ? 'rgba(16,185,129,0.10)' : 'rgba(16,185,129,0.06)',
                                borderColor: 'rgba(16,185,129,0.30)',
                              }}
                            >
                              <div className={`am-eyebrow mb-1 text-emerald-500`}>KILLER QUE LO RESUELVE</div>
                              <p className={`am-serif text-sm font-medium ${text} leading-relaxed`}>{trap.killer}</p>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </motion.div>
          </AnimatePresence>
        )}

        {/* TAB: INSIGHTS DE ESTUDIO */}
        {activeTab === 'study' && analysis.studyInsights && (
          <AnimatePresence mode="wait">
            <motion.div
              key="study"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="grid md:grid-cols-2 gap-4"
            >
              {/* Prioridad de inversión */}
              <div className={`${innerBg} rounded-2xl p-5`}>
                <div className={`am-eyebrow mb-3 text-rose-500`}>
                  <Target className="w-3 h-3 inline mr-1.5" />
                  PRIORIDAD DE INVERSIÓN
                </div>
                <ol className="space-y-2">
                  {analysis.studyInsights.investmentPriority?.map((p, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className={`am-numeric text-xl flex-shrink-0`} style={{ color: PALETTE.danger.fill }}>{p.rank}°</span>
                      <div>
                        <div className={`am-serif font-semibold ${text}`}>{p.topic}</div>
                        <div className={`am-sans text-xs ${subText}`}>{p.reason}</div>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>

              {/* Estructura de estudio */}
              <div className={`${innerBg} rounded-2xl p-5`}>
                <div className={`am-eyebrow mb-3 text-indigo-500`}>
                  <Layers className="w-3 h-3 inline mr-1.5" />
                  ESTRUCTURA DE ESTUDIO POR PARÁSITO
                </div>
                <ul className={`am-serif text-sm space-y-1.5 ${text}`}>
                  {analysis.studyInsights.studyStructure?.map((s, i) => (
                    <li key={i}>{s}</li>
                  ))}
                </ul>
              </div>

              {/* Skip list */}
              <div className={`${innerBg} rounded-2xl p-5`}>
                <div className={`am-eyebrow mb-3 ${muted}`}>
                  ❌ NO PERDER TIEMPO
                </div>
                <div className="space-y-2">
                  {analysis.studyInsights.skipList?.map((s, i) => (
                    <div key={i} className={`p-2 rounded-lg ${darkMode ? 'bg-slate-800/50' : 'bg-white border border-slate-200'}`}>
                      <div className={`am-serif text-sm font-semibold ${text}`}>{s.item}</div>
                      <div className={`am-sans text-xs italic ${subText}`}>{s.reason}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Always ready */}
              <div
                className={`rounded-2xl p-5 border`}
                style={{
                  background: darkMode ? 'rgba(16,185,129,0.10)' : 'rgba(16,185,129,0.06)',
                  borderColor: 'rgba(16,185,129,0.30)',
                }}
              >
                <div className={`am-eyebrow mb-3 text-emerald-500`}>
                  <Sparkles className="w-3 h-3 inline mr-1.5" />
                  SIEMPRE TENER PREPARADO
                </div>
                <ul className={`am-serif text-sm space-y-1.5 ${text}`}>
                  {analysis.studyInsights.alwaysReady?.map((a, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-emerald-500 mt-0.5">✓</span>
                      <span>{a}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </AnimatePresence>
        )}

      </div>
    </motion.section>
  );
}

// ============================================================================
// StrategicWorkspace — workspace master-detail tipo Notion para predicciones
// ============================================================================
const STATUS_META = {
  pending:    { label: 'Pendiente',   color: '#94a3b8', icon: '○' },
  analyzing:  { label: 'En análisis', color: '#f59e0b', icon: '⚙' },
  completed:  { label: 'Completado',  color: '#10b981', icon: '✓' },
};

function StrategicWorkspace({ predictions, tierMeta, darkMode, onPredictionClick, areaName, dataAnalysis }) {
  const text = darkMode ? 'text-slate-100' : 'text-slate-900';
  const subText = darkMode ? 'text-slate-400' : 'text-slate-500';
  const muted = darkMode ? 'text-slate-500' : 'text-slate-400';
  const innerBg = darkMode ? 'bg-slate-900/40' : 'bg-slate-50/80';
  const cardBg = darkMode ? 'bg-slate-800/40' : 'bg-white border border-slate-200';

  const themesData = useMemo(() => {
    const byTopic = {};
    for (const p of predictions) {
      if (!byTopic[p.topic]) {
        byTopic[p.topic] = {
          topic: p.topic, items: [], totalScore: 0, maxProb: 0,
          muyAltaCount: 0, altaCount: 0, mediaCount: 0,
        };
      }
      byTopic[p.topic].items.push(p);
      byTopic[p.topic].totalScore += p.probability;
      byTopic[p.topic].maxProb = Math.max(byTopic[p.topic].maxProb, p.probability);
      if (p.tier === 'MUY_ALTA') byTopic[p.topic].muyAltaCount++;
      else if (p.tier === 'ALTA') byTopic[p.topic].altaCount++;
      else byTopic[p.topic].mediaCount++;
    }
    if (dataAnalysis?.topicMatrix) {
      for (const t of dataAnalysis.topicMatrix) {
        if (!byTopic[t.topic]) {
          byTopic[t.topic] = {
            topic: t.topic, items: [], totalScore: 0, maxProb: 0,
            muyAltaCount: 0, altaCount: 0, mediaCount: 0, historic: t.total,
          };
        } else {
          byTopic[t.topic].historic = t.total;
        }
      }
    }
    return Object.values(byTopic).sort((a, b) => b.totalScore - a.totalScore);
  }, [predictions, dataAnalysis]);

  const [selectedTopic, setSelectedTopic] = useState(themesData[0]?.topic);
  const [progressMap, setProgressMap] = useState({});

  useEffect(() => {
    try {
      const saved = localStorage.getItem(`mente-progress-${areaName}`);
      if (saved) setProgressMap(JSON.parse(saved));
    } catch {}
  }, [areaName]);

  const setStatus = (topic, status) => {
    const next = { ...progressMap, [topic]: status };
    setProgressMap(next);
    try {
      localStorage.setItem(`mente-progress-${areaName}`, JSON.stringify(next));
    } catch {}
  };

  const getStatus = (topic) => progressMap[topic] || 'pending';

  const stats = {
    total: themesData.length,
    completed: themesData.filter(t => getStatus(t.topic) === 'completed').length,
    analyzing: themesData.filter(t => getStatus(t.topic) === 'analyzing').length,
    pending: themesData.filter(t => getStatus(t.topic) === 'pending').length,
  };

  const themePrio = (t) => {
    if (t.muyAltaCount >= 2) return { label: 'PRIORIDAD MÁXIMA', color: PALETTE.danger.fill };
    if (t.muyAltaCount >= 1 && t.totalScore >= 150) return { label: 'PRIORIDAD ALTA', color: PALETTE.warning.fill };
    if (t.muyAltaCount >= 1) return { label: 'PRIORIDAD ALTA', color: PALETTE.warning.fill };
    if (t.altaCount >= 1) return { label: 'PRIORIDAD MEDIA', color: PALETTE.info.fill };
    if (t.items.length > 0) return { label: 'BACKUP', color: PALETTE.success.fill };
    return { label: 'SIN PREDICCIONES', color: '#64748b' };
  };

  const selected = themesData.find(t => t.topic === selectedTopic);
  const selectedPrio = selected ? themePrio(selected) : null;

  return (
    <div className="space-y-4">
      <div className={`p-5 rounded-2xl ${innerBg}`}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className={`am-eyebrow mb-1`} style={{ color: PALETTE.danger.fill }}>
              <Crosshair className="w-3 h-3 inline mr-2" />
              WORKSPACE ESTRATÉGICO · ATAQUE TEMA POR TEMA
            </div>
            <p className={`am-serif text-sm leading-relaxed ${text}`}>
              Selecciona un tema del sidebar y analiza sus subtemas predichos. <span className={subText}>Marca tu progreso para llevar el control.</span>
            </p>
          </div>
          <div className="flex items-center gap-4">
            <ProgressStat icon="✓" label="Completados" value={stats.completed} total={stats.total} color={STATUS_META.completed.color} />
            <ProgressStat icon="⚙" label="En análisis" value={stats.analyzing} total={stats.total} color={STATUS_META.analyzing.color} />
            <ProgressStat icon="○" label="Pendientes" value={stats.pending} total={stats.total} color={STATUS_META.pending.color} />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">

        <div className={`lg:col-span-4 ${innerBg} rounded-2xl p-4 max-h-[800px] am-scroll overflow-y-auto`}>
          <div className={`am-eyebrow mb-3 ${muted} px-2`}>
            {themesData.length} TEMAS · ORDEN POR PRIORIDAD
          </div>
          <div className="space-y-1.5">
            {themesData.map((t, i) => {
              const prio = themePrio(t);
              const status = getStatus(t.topic);
              const statusMeta = STATUS_META[status];
              const isSelected = t.topic === selectedTopic;
              const totalSubs = t.items.length;

              return (
                <motion.button
                  key={t.topic}
                  initial={{ opacity: 0, x: -6 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: 0.03 * i }}
                  onClick={() => setSelectedTopic(t.topic)}
                  className={`am-focus w-full text-left p-3 rounded-xl transition-all relative ${
                    isSelected
                      ? darkMode ? 'bg-slate-700/60' : 'bg-white shadow-md'
                      : darkMode ? 'hover:bg-slate-800/40' : 'hover:bg-white/60'
                  }`}
                  style={isSelected ? { borderLeft: `3px solid ${prio.color}` } : { borderLeft: `3px solid transparent` }}
                >
                  <div className="flex items-start gap-2">
                    <span
                      className={`am-mono text-xs flex-shrink-0 mt-0.5`}
                      style={{ color: statusMeta.color }}
                      title={statusMeta.label}
                    >
                      {statusMeta.icon}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2 mb-1">
                        <span className={`am-mono text-[10px] ${muted} flex-shrink-0`}>
                          #{String(i + 1).padStart(2, '0')}
                        </span>
                        <span className={`am-serif text-sm font-medium truncate ${text}`}>
                          {t.topic}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <span
                          className="am-eyebrow text-[8px] px-1.5 py-0.5 rounded-full whitespace-nowrap"
                          style={{ background: `${prio.color}15`, color: prio.color, border: `1px solid ${prio.color}30` }}
                        >
                          {prio.label}
                        </span>
                        {totalSubs > 0 && (
                          <span className={`am-numeric text-base flex-shrink-0`} style={{ color: prio.color }}>
                            {t.totalScore}
                          </span>
                        )}
                      </div>
                      {totalSubs > 0 && (
                        <div className={`flex h-1 rounded-full overflow-hidden mt-2 ${darkMode ? 'bg-slate-800' : 'bg-slate-200'}`}>
                          {t.muyAltaCount > 0 && <div style={{ background: PALETTE.danger.fill, width: `${(t.muyAltaCount / totalSubs) * 100}%` }} />}
                          {t.altaCount > 0 && <div style={{ background: PALETTE.warning.fill, width: `${(t.altaCount / totalSubs) * 100}%` }} />}
                          {t.mediaCount > 0 && <div style={{ background: PALETTE.info.fill, width: `${(t.mediaCount / totalSubs) * 100}%` }} />}
                        </div>
                      )}
                    </div>
                  </div>
                </motion.button>
              );
            })}
          </div>
        </div>

        <div className="lg:col-span-8">
          {selected && selectedPrio && (
            <AnimatePresence mode="wait">
              <motion.div
                key={selected.topic}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.3 }}
                className="space-y-4"
              >
                <div className={`${innerBg} rounded-2xl p-6 relative overflow-hidden`} style={{ borderLeft: `4px solid ${selectedPrio.color}` }}>
                  <div className="flex items-start gap-4 mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span
                          className="am-eyebrow text-[10px] px-2 py-0.5 rounded-full"
                          style={{ background: `${selectedPrio.color}15`, color: selectedPrio.color, border: `1px solid ${selectedPrio.color}30` }}
                        >
                          {selectedPrio.label}
                        </span>
                        {selected.items.length > 0 && (
                          <span className={`am-mono text-[10px] ${muted}`}>
                            score {selected.totalScore} · {selected.items.length} subtema{selected.items.length > 1 ? 's' : ''} predicho{selected.items.length > 1 ? 's' : ''}
                          </span>
                        )}
                        {selected.historic && (
                          <span className={`am-mono text-[10px] ${muted}`}>
                            · {selected.historic} histórica{selected.historic > 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                      <h3 className={`am-display text-3xl font-medium ${text}`}>
                        {selected.topic}
                      </h3>
                    </div>
                    {selected.items.length > 0 && (
                      <div className="text-right flex-shrink-0">
                        <div className={`am-numeric text-6xl`} style={{ color: selectedPrio.color }}>
                          {selected.maxProb}
                        </div>
                        <div className={`am-mono text-[10px] ${muted}`}>% top sub</div>
                      </div>
                    )}
                  </div>

                  {selected.items.length > 0 && (
                    <div>
                      <div className={`flex h-2 rounded-full overflow-hidden ${darkMode ? 'bg-slate-800' : 'bg-slate-200'}`}>
                        {selected.muyAltaCount > 0 && <div style={{ background: PALETTE.danger.fill, width: `${(selected.muyAltaCount / selected.items.length) * 100}%` }} />}
                        {selected.altaCount > 0 && <div style={{ background: PALETTE.warning.fill, width: `${(selected.altaCount / selected.items.length) * 100}%` }} />}
                        {selected.mediaCount > 0 && <div style={{ background: PALETTE.info.fill, width: `${(selected.mediaCount / selected.items.length) * 100}%` }} />}
                      </div>
                      <div className={`am-mono text-[9px] mt-1.5 flex gap-3 ${muted}`}>
                        {selected.muyAltaCount > 0 && <span><span className="am-dot mr-1" style={{ background: PALETTE.danger.fill }} />{selected.muyAltaCount} MUY ALTA</span>}
                        {selected.altaCount > 0 && <span><span className="am-dot mr-1" style={{ background: PALETTE.warning.fill }} />{selected.altaCount} ALTA</span>}
                        {selected.mediaCount > 0 && <span><span className="am-dot mr-1" style={{ background: PALETTE.info.fill }} />{selected.mediaCount} MEDIA</span>}
                      </div>
                    </div>
                  )}
                </div>

                {selected.items.length > 0 ? (
                  <div className={`${innerBg} rounded-2xl p-6`}>
                    <div className={`am-eyebrow mb-4 ${muted}`}>
                      <Target className="w-3 h-3 inline mr-2" />
                      SUBTEMAS A ATACAR · CLICK PARA ANÁLISIS MICRO
                    </div>
                    <div className="space-y-2.5">
                      {selected.items.map((p, i) => {
                        const tier = tierMeta[p.tier] || tierMeta.MEDIA;
                        return (
                          <motion.button
                            key={p.rank}
                            initial={{ opacity: 0, x: -6 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.3, delay: 0.04 * i }}
                            onClick={() => onPredictionClick(p)}
                            className={`am-focus am-lift w-full text-left rounded-xl p-4 group transition-colors ${cardBg} ${darkMode ? 'hover:bg-slate-800/70' : 'hover:bg-slate-50'}`}
                            style={{ borderLeft: `2px solid ${tier.color}` }}
                          >
                            <div className="flex items-start gap-3">
                              <div className="flex flex-col items-center flex-shrink-0 w-12">
                                <div className={`am-numeric text-2xl`} style={{ color: tier.color }}>{p.probability}</div>
                                <div className={`am-mono text-[8px] ${muted}`}>%</div>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                  <span
                                    className="am-eyebrow text-[8px] px-1.5 py-0.5 rounded-full"
                                    style={{ background: tier.bg, color: tier.color, border: `1px solid ${tier.color}30` }}
                                  >
                                    {tier.label}
                                  </span>
                                </div>
                                <h5 className={`am-serif text-base font-medium ${text}`}>{p.subject}</h5>
                                <p className={`am-sans text-xs mt-1 leading-relaxed ${subText} line-clamp-2`}>{p.reasoning}</p>
                                {p.killerToRemember && (
                                  <div
                                    className="mt-2 px-2.5 py-1.5 rounded-md text-xs leading-snug"
                                    style={{
                                      background: darkMode ? 'rgba(16,185,129,0.10)' : 'rgba(16,185,129,0.06)',
                                      border: '1px solid rgba(16,185,129,0.30)',
                                    }}
                                  >
                                    <span className={`am-eyebrow text-[8px] mr-1.5 text-emerald-500`}>KILLER:</span>
                                    <span className={`am-serif ${text}`}>{p.killerToRemember}</span>
                                  </div>
                                )}
                              </div>
                              <ChevronRight className={`w-3.5 h-3.5 ${muted} flex-shrink-0 mt-1 group-hover:translate-x-1 transition-transform`} />
                            </div>
                          </motion.button>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className={`${innerBg} rounded-2xl p-8 text-center`}>
                    <p className={`am-serif italic ${subText}`}>
                      Este tema no tiene predicciones específicas en el análisis del experto. Tiene {selected.historic || 0} pregunta{selected.historic !== 1 ? 's' : ''} histórica{selected.historic !== 1 ? 's' : ''} en banco — vista BACKUP.
                    </p>
                  </div>
                )}

                <div className={`${innerBg} rounded-2xl p-6`}>
                  <div className={`am-eyebrow mb-4 ${muted}`}>
                    <Sparkles className="w-3 h-3 inline mr-2" />
                    TU PROGRESO EN ESTE TEMA
                  </div>
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl" style={{ color: STATUS_META[getStatus(selected.topic)].color }}>
                        {STATUS_META[getStatus(selected.topic)].icon}
                      </span>
                      <div>
                        <div className={`am-eyebrow ${muted}`}>ESTADO ACTUAL</div>
                        <div className={`am-display text-xl ${text}`}>
                          {STATUS_META[getStatus(selected.topic)].label}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <ProgressBtn active={getStatus(selected.topic) === 'pending'} onClick={() => setStatus(selected.topic, 'pending')} color={STATUS_META.pending.color} icon="○" label="Pendiente" darkMode={darkMode} />
                      <ProgressBtn active={getStatus(selected.topic) === 'analyzing'} onClick={() => setStatus(selected.topic, 'analyzing')} color={STATUS_META.analyzing.color} icon="⚙" label="En análisis" darkMode={darkMode} />
                      <ProgressBtn active={getStatus(selected.topic) === 'completed'} onClick={() => setStatus(selected.topic, 'completed')} color={STATUS_META.completed.color} icon="✓" label="Completado" darkMode={darkMode} />
                    </div>
                  </div>
                  <div className={`am-mono text-[10px] mt-4 ${muted}`}>
                    Tu progreso se guarda en el navegador. Sirve para llevar control de qué temas ya trabajaste sistemáticamente.
                  </div>
                </div>

              </motion.div>
            </AnimatePresence>
          )}
        </div>

      </div>
    </div>
  );
}

function ProgressStat({ icon, label, value, total, color }) {
  return (
    <div className="flex items-center gap-2">
      <span style={{ color }} className="text-lg">{icon}</span>
      <div>
        <div className="am-numeric text-base" style={{ color }}>
          {value}<span className="am-mono text-[10px] opacity-60 ml-0.5">/{total}</span>
        </div>
        <div className="am-mono text-[9px] uppercase tracking-widest opacity-60">{label}</div>
      </div>
    </div>
  );
}

function ProgressBtn({ active, onClick, color, icon, label, darkMode }) {
  return (
    <button
      onClick={onClick}
      className={`am-focus flex items-center gap-2 px-3 py-2 rounded-lg transition-all am-mono text-xs font-semibold ${
        active
          ? 'text-white'
          : darkMode ? 'bg-slate-800/60 text-slate-400 hover:bg-slate-800' : 'bg-white text-slate-500 hover:bg-slate-100 border border-slate-200'
      }`}
      style={active ? { background: color } : {}}
    >
      <span>{icon}</span>
      <span>{label}</span>
    </button>
  );
}

// ============================================================================
// DataAndAnalysisSection — evidencia cruda con comentarios estratégicos
// ============================================================================

function DataAndAnalysisSection({ data, darkMode, areaName, totalQuestions, onSubtopicClick }) {
  const text = darkMode ? 'text-slate-100' : 'text-slate-900';
  const subText = darkMode ? 'text-slate-400' : 'text-slate-500';
  const muted = darkMode ? 'text-slate-500' : 'text-slate-400';
  const innerBg = darkMode ? 'bg-slate-900/40' : 'bg-slate-50/80';
  const stratBg = darkMode ? 'bg-purple-500/8 border-purple-500/30' : 'bg-purple-50 border-purple-200';

  const maxCellValue = Math.max(...data.topicMatrix.flatMap(row => data.years.map(y => row[y])));
  const cellColor = (v) => {
    if (v === 0) return 'transparent';
    const intensity = Math.min(1, v / maxCellValue);
    return darkMode
      ? `rgba(99, 102, 241, ${0.15 + intensity * 0.5})`
      : `rgba(99, 102, 241, ${0.08 + intensity * 0.45})`;
  };

  // Análisis estratégico computado on-the-fly
  const sortedTopics = [...data.topicMatrix].slice(0, 3);
  const top3Names = sortedTopics.map(t => t.topic).join(', ');
  const recentBoom = data.topicMatrix.find(t => {
    const last5 = data.years.slice(-5).reduce((a, y) => a + t[y], 0);
    const prev5 = data.years.slice(-10, -5).reduce((a, y) => a + t[y], 0);
    return last5 - prev5 >= 2;
  });
  const recentDecline = data.topicMatrix.find(t => {
    const last5 = data.years.slice(-5).reduce((a, y) => a + t[y], 0);
    const prev5 = data.years.slice(-10, -5).reduce((a, y) => a + t[y], 0);
    return prev5 - last5 >= 2;
  });
  const yearTotals2025 = data.yearTotals.find(y => y.year === 'RM 2025');
  const angleTop = Object.entries(data.angles).sort((a, b) => b[1] - a[1])[0];
  const angleTopPct = ((angleTop[1] / totalQuestions) * 100).toFixed(0);
  const localizacionPct = ((data.angles.LOCALIZACION / totalQuestions) * 100).toFixed(1);
  const gapsBig = data.allSubtopics.filter(s => s.gap >= 10).length;
  const gapsRecord = data.allSubtopics.filter(s => s.gap >= 13).length;
  const fixedPool = data.topDistractors.filter(d => d.count >= 3).length;

  return (
    <>
      {/* Hero de la sección */}
      <div className={`p-5 rounded-2xl ${innerBg}`}>
        <div className={`am-eyebrow mb-2`} style={{ color: PALETTE.purple.fill }}>
          <Layers className="w-3 h-3 inline mr-2" />
          EVIDENCIA + ANÁLISIS ESTRATÉGICO
        </div>
        <p className={`am-serif text-base leading-relaxed ${text}`}>
          La data cruda que sustenta cada conclusión del experto. <span className={subText}>Cada tabla viene con análisis estratégico contextual.</span>
        </p>
      </div>

      {/* TABLA 01: Matriz tema × año */}
      <div className={`p-6 rounded-2xl ${innerBg}`}>
        <div className="mb-4">
          <div className={`am-eyebrow mb-2 ${muted}`}>TABLA 01 · MATRIZ TEMA × AÑO</div>
          <h4 className={`am-display text-xl font-medium ${text}`}>Heatmap completo 2010–2025</h4>
        </div>
        <div className="am-scroll overflow-x-auto rounded-xl">
          <table className="w-full text-xs">
            <thead>
              <tr>
                <th className={`text-left p-2 ${muted} am-mono uppercase tracking-wider sticky left-0 ${innerBg} z-10`}>Tema</th>
                {data.years.map(y => (
                  <th key={y} className={`p-2 text-center am-mono ${muted}`}>{y.replace('RM ', '')}</th>
                ))}
                <th className={`p-2 text-right am-mono uppercase tracking-wider ${text} font-bold`}>Σ</th>
              </tr>
            </thead>
            <tbody>
              {data.topicMatrix.map(row => (
                <tr key={row.topic} className={`${darkMode ? 'hover:bg-slate-800/30' : 'hover:bg-slate-50'} transition-colors`}>
                  <td className={`p-2 am-serif text-sm ${text} sticky left-0 ${innerBg} z-10 truncate max-w-[180px]`}>{row.topic}</td>
                  {data.years.map(y => (
                    <td
                      key={y}
                      className={`p-2 text-center am-mono am-tabular`}
                      style={{
                        background: cellColor(row[y]),
                        color: row[y] > 0 ? (darkMode ? '#fff' : '#0f172a') : (darkMode ? '#475569' : '#cbd5e1'),
                        fontWeight: row[y] >= 3 ? 700 : 400,
                      }}
                    >
                      {row[y] > 0 ? row[y] : '·'}
                    </td>
                  ))}
                  <td className={`p-2 text-right am-numeric ${text} font-bold`}>{row.total}</td>
                </tr>
              ))}
              <tr className={`${darkMode ? 'border-t border-slate-700' : 'border-t border-slate-200'}`}>
                <td className={`p-2 am-eyebrow ${muted} sticky left-0 ${innerBg} z-10`}>TOTAL/AÑO</td>
                {data.yearTotals.map(y => (
                  <td key={y.year} className={`p-2 text-center am-numeric text-sm ${text}`}>{y.total}</td>
                ))}
                <td className={`p-2 text-right am-numeric ${text} font-bold text-base`}>{totalQuestions}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className={`mt-4 p-4 rounded-xl border ${stratBg}`}>
          <div className={`am-eyebrow mb-2`} style={{ color: PALETTE.purple.text }}>
            <Sparkles className="w-3 h-3 inline mr-2" />
            ANÁLISIS ESTRATÉGICO
          </div>
          <ul className={`am-serif text-sm space-y-2 ${text} leading-relaxed`}>
            <li>• <strong>Top 3 temas (acumulan {sortedTopics.reduce((a, t) => a + t.total, 0)} de {totalQuestions} preg, {Math.round(sortedTopics.reduce((a, t) => a + t.total, 0) / totalQuestions * 100)}%):</strong> {top3Names}. Ahí va el grueso del estudio.</li>
            {recentBoom && <li>• <strong>Tema en alza últimos 5 años:</strong> {recentBoom.topic}. El examinador le está dando más peso recientemente — esperar tendencia continúe en 2026.</li>}
            {recentDecline && <li>• <strong>Tema en declive:</strong> {recentDecline.topic}. Cae menos en últimos 5 años. Posible delegación a otra área o pérdida de prioridad.</li>}
            <li>• <strong>RM 2025 cayó {yearTotals2025?.total || 0} preguntas total.</strong> Si fue alto, el examinador descargó mucho — ojo con compensación contraria. Si fue bajo, espacios para temas pendientes.</li>
            <li>• <strong>Celdas vacías son oportunidades:</strong> tema con 5+ años seguidos sin caer = candidato fuerte para 2026.</li>
          </ul>
        </div>
      </div>

      {/* TABLA 02 + 03 grid */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className={`p-6 rounded-2xl ${innerBg}`}>
          <div className="mb-4">
            <div className={`am-eyebrow mb-2 ${muted}`}>TABLA 02 · ÁNGULOS</div>
            <h4 className={`am-display text-xl font-medium ${text}`}>Cómo enfoca</h4>
          </div>
          <div className="space-y-2">
            {Object.entries(data.angles).sort((a, b) => b[1] - a[1]).map(([angle, count]) => {
              const pct = ((count / totalQuestions) * 100).toFixed(1);
              return (
                <div key={angle} className="flex items-center gap-3">
                  <div className={`am-mono text-[10px] uppercase tracking-wider w-28 ${subText}`}>{angle}</div>
                  <div className={`flex-1 h-2 rounded-full overflow-hidden ${darkMode ? 'bg-slate-800' : 'bg-slate-200'}`}>
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${pct}%` }}
                      transition={{ duration: 0.6 }}
                      className="h-full rounded-full"
                      style={{ background: PALETTE.purple.fill }}
                    />
                  </div>
                  <div className={`am-numeric text-sm w-10 text-right ${text}`}>{count}</div>
                  <div className={`am-mono text-[10px] w-10 text-right ${muted}`}>{pct}%</div>
                </div>
              );
            })}
          </div>
          <div className={`mt-4 p-3 rounded-xl border ${stratBg}`}>
            <div className={`am-eyebrow mb-1`} style={{ color: PALETTE.purple.text }}>
              <Sparkles className="w-3 h-3 inline mr-1.5" /> LECTURA
            </div>
            <p className={`am-serif text-sm ${text} leading-snug`}>
              <strong>{angleTop[0]}</strong> domina con {angleTopPct}% — su obsesión #1.
              {data.angles.LOCALIZACION === 1 && ` LOCALIZACIÓN aparece SOLO 1 vez (Trichuris 2013) — si reaparece, será un golpe inesperado, considerar como predicción de alta sorpresa.`}
              {data.angles.LOCALIZACION > 1 && ` LOCALIZACIÓN ${localizacionPct}% — frecuencia baja pero existe.`}
            </p>
          </div>
        </div>

        <div className={`p-6 rounded-2xl ${innerBg}`}>
          <div className="mb-4">
            <div className={`am-eyebrow mb-2 ${muted}`}>TABLA 03 · CASOS vs DIRECTAS</div>
            <h4 className={`am-display text-xl font-medium ${text}`}>Formato por tema</h4>
          </div>
          <div className="am-scroll max-h-72 overflow-y-auto space-y-1">
            {data.casosTema.map((t) => {
              const casoPct = t.total > 0 ? (t.casos / t.total) * 100 : 0;
              return (
                <div key={t.topic} className="flex items-center gap-2">
                  <div className={`am-serif text-xs ${text} w-28 truncate`}>{t.topic}</div>
                  <div className={`flex-1 h-3 rounded-full overflow-hidden flex ${darkMode ? 'bg-slate-800' : 'bg-slate-200'}`}>
                    <div className="h-full" style={{ width: `${casoPct}%`, background: PALETTE.info.fill }} />
                    <div className="h-full" style={{ width: `${100 - casoPct}%`, background: PALETTE.success.fill }} />
                  </div>
                  <div className={`am-mono text-[10px] w-14 text-right ${muted}`}>{t.casos}c·{t.directas}d</div>
                </div>
              );
            })}
          </div>
          <div className={`mt-4 p-3 rounded-xl border ${stratBg}`}>
            <div className={`am-eyebrow mb-1`} style={{ color: PALETTE.purple.text }}>
              <Sparkles className="w-3 h-3 inline mr-1.5" /> LECTURA
            </div>
            <p className={`am-serif text-sm ${text} leading-snug`}>
              Caso clínico = espera <strong>killer patognomónico</strong> + procedencia. Directa = espera <strong>lista mnemotécnica</strong> (NASA, NATA, etiología por edad). El formato te dice qué tipo de respuesta tener lista.
            </p>
          </div>
        </div>
      </div>

      {/* TABLA 04: Todos los subtemas con gap */}
      <div className={`p-6 rounded-2xl ${innerBg}`}>
        <div className="mb-4">
          <div className={`am-eyebrow mb-2 ${muted}`}>TABLA 04 · {data.allSubtopics.length} SUBTEMAS · GAP COMPLETO</div>
          <h4 className={`am-display text-xl font-medium ${text}`}>Todos los subtemas ordenados por gap</h4>
          <p className={`am-sans text-xs mt-1 ${subText}`}>Click cualquier fila → drill MICRO directo</p>
        </div>
        <div className="am-scroll max-h-[500px] overflow-y-auto rounded-xl">
          <table className="w-full text-xs">
            <thead className={`sticky top-0 ${innerBg} z-10`}>
              <tr>
                <th className={`text-left p-2 ${muted} am-mono uppercase tracking-wider`}>#</th>
                <th className={`text-left p-2 ${muted} am-mono uppercase tracking-wider`}>Subtema</th>
                <th className={`text-left p-2 ${muted} am-mono uppercase tracking-wider`}>Tema</th>
                <th className={`text-center p-2 ${muted} am-mono uppercase tracking-wider`}>×</th>
                <th className={`text-center p-2 ${muted} am-mono uppercase tracking-wider`}>Último</th>
                <th className={`text-right p-2 ${muted} am-mono uppercase tracking-wider`}>Gap</th>
              </tr>
            </thead>
            <tbody>
              {data.allSubtopics.map((s, i) => {
                const gapColor = s.gap >= 13 ? PALETTE.danger.fill : s.gap >= 8 ? PALETTE.warning.fill : s.gap >= 5 ? PALETTE.info.fill : PALETTE.success.fill;
                return (
                  <tr
                    key={s.subtopic + i}
                    onClick={() => onSubtopicClick && onSubtopicClick(s.topic, s.subtopic)}
                    className={`cursor-pointer ${darkMode ? 'hover:bg-slate-800/40' : 'hover:bg-slate-100'} transition-colors border-b ${darkMode ? 'border-slate-800' : 'border-slate-100'}`}
                  >
                    <td className={`p-2 am-mono ${muted}`}>{String(i + 1).padStart(3, '0')}</td>
                    <td className={`p-2 am-serif ${text}`}>{s.subtopic}</td>
                    <td className={`p-2 am-sans text-[11px] ${subText} truncate max-w-[160px]`}>{s.topic}</td>
                    <td className={`p-2 text-center am-numeric ${text}`}>{s.count}</td>
                    <td className={`p-2 text-center am-mono ${subText}`}>{s.lastYear?.replace('RM ', '')}</td>
                    <td className={`p-2 text-right am-numeric font-bold`} style={{ color: gapColor }}>{s.gap}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className={`mt-4 p-4 rounded-xl border ${stratBg}`}>
          <div className={`am-eyebrow mb-2`} style={{ color: PALETTE.purple.text }}>
            <Sparkles className="w-3 h-3 inline mr-2" />
            ANÁLISIS ESTRATÉGICO
          </div>
          <ul className={`am-serif text-sm space-y-2 ${text} leading-relaxed`}>
            <li>• <strong>{gapsRecord} subtemas con gap ≥13 años</strong> (récords absolutos del banco). Estos son los más probables 2026 si caen del tema.</li>
            <li>• <strong>{gapsBig} subtemas con gap ≥10 años.</strong> Tu material de estudio debería priorizar estos como "candidatos fuertes".</li>
            <li>• <strong>Ojo con subtemas que tienen muchas apariciones (×3+) pero gap reciente:</strong> son "estrella" del examinador, esperar variantes.</li>
            <li>• <strong>Subtemas con gap ∞ (no listados aquí porque nunca cayeron):</strong> están en las predicciones del experto. Ej: triclabendazol Fasciola.</li>
          </ul>
        </div>
      </div>

      {/* TABLA 05: Pool global de distractores */}
      <div className={`p-6 rounded-2xl ${innerBg}`}>
        <div className="mb-4">
          <div className={`am-eyebrow mb-2 ${muted}`}>TABLA 05 · POOL GLOBAL DISTRACTORES</div>
          <h4 className={`am-display text-xl font-medium ${text}`}>Top 30 distractores recurrentes</h4>
        </div>
        <div className="grid md:grid-cols-2 gap-2">
          {data.topDistractors.map((d, i) => (
            <div key={i} className={`flex items-center gap-3 px-3 py-2 rounded-lg ${darkMode ? 'bg-slate-800/40' : 'bg-white border border-slate-200'}`}>
              <span className={`am-mono text-[10px] ${muted} w-8`}>#{String(i + 1).padStart(2, '0')}</span>
              <span className={`am-numeric text-base ${d.count >= 3 ? 'text-rose-500' : d.count >= 2 ? 'text-amber-500' : muted}`}>×{d.count}</span>
              <span className={`am-serif text-sm flex-1 truncate ${text}`}>{d.text}</span>
              <span className={`am-mono text-[9px] ${muted}`}>{d.topics.length}t</span>
            </div>
          ))}
        </div>
        <div className={`mt-4 p-4 rounded-xl border ${stratBg}`}>
          <div className={`am-eyebrow mb-2`} style={{ color: PALETTE.purple.text }}>
            <Sparkles className="w-3 h-3 inline mr-2" />
            ANÁLISIS ESTRATÉGICO
          </div>
          <ul className={`am-serif text-sm space-y-2 ${text} leading-relaxed`}>
            <li>• <strong>{fixedPool} distractores aparecen 3+ veces</strong> (rojo). Son el <strong>pool fijo</strong> del examinador. No los inventa, los rota.</li>
            <li>• <strong>Cuando confeccionemos simulacros</strong>, los distractores deben venir DE ESTE POOL — así clonan al examinador real.</li>
            <li>• <strong>Distractores que aparecen en 3+ temas distintos</strong> son los favoritos universales (E. coli, opciones genéricas que parecen correctas).</li>
            <li>• <strong>Implicación clínica:</strong> los distractores no son aleatorios — el examinador escoge entidades que un estudiante CONFUNDIRÍA con la correcta. Estudiar las trampas vale tanto como estudiar la respuesta.</li>
          </ul>
        </div>
      </div>

      {/* TABLA 06: Queries SQL */}
      <div className={`p-6 rounded-2xl ${innerBg}`}>
        <div className="mb-4">
          <div className={`am-eyebrow mb-2 ${muted}`}>TABLA 06 · TRANSPARENCIA METODOLÓGICA</div>
          <h4 className={`am-display text-xl font-medium ${text}`}>Queries SQL · reproducibles</h4>
          <p className={`am-sans text-xs mt-1 ${subText}`}>Cada conclusión está basada en estas queries · Copialas y corrélas en MySQL Workbench</p>
        </div>
        <div className="space-y-3">
          <SqlBlock
            title="Distribución tema × año (Tabla 01)"
            query={`SELECT topic, year, COUNT(*) as count
FROM questions
WHERE area = '${areaName}' AND year LIKE 'RM%'
GROUP BY topic, year
ORDER BY topic, year;`}
            darkMode={darkMode}
          />
          <SqlBlock
            title="Subtemas con gap (Tabla 04)"
            query={`SELECT subtopic_specific, COUNT(*) as veces,
  MAX(year) as ultimo,
  (2026 - CAST(SUBSTRING(MAX(year), 4) AS UNSIGNED)) as gap
FROM questions
WHERE area = '${areaName}' AND year LIKE 'RM%'
GROUP BY subtopic_specific ORDER BY gap DESC;`}
            darkMode={darkMode}
          />
          <SqlBlock
            title="Pool global de distractores (Tabla 05)"
            query={`SELECT o.option_text, COUNT(*) as veces
FROM questions q JOIN options o ON q.id = o.question_id
WHERE q.area = '${areaName}' AND o.correct = 0
GROUP BY o.option_text
ORDER BY veces DESC LIMIT 30;`}
            darkMode={darkMode}
          />
        </div>
      </div>
    </>
  );
}

function TopicMacroCard({ topic, index, darkMode, onClick }) {
  const text = darkMode ? 'text-slate-100' : 'text-slate-900';
  const subText = darkMode ? 'text-slate-400' : 'text-slate-500';
  const muted = darkMode ? 'text-slate-500' : 'text-slate-400';

  const userAcc = topic.userAccuracy;
  const accent = userAcc !== null ? accuracyColor(userAcc) : '#64748b';
  const tier = userAcc !== null
    ? userAcc >= 70 ? 'DOMINADO' : userAcc >= 50 ? 'EN PROGRESO' : userAcc >= 30 ? 'FRÁGIL' : 'CRÍTICO'
    : 'NO EVALUADO';

  return (
    <motion.button
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.04 * index, ease: [0.2, 0.8, 0.2, 1] }}
      whileHover={{ y: -4 }}
      onClick={onClick}
      className="am-focus am-noise am-tier-card rounded-2xl p-5 text-left relative overflow-hidden group transition-all duration-300"
      style={{
        background: darkMode
          ? `radial-gradient(ellipse 80% 60% at top right, ${accent}22 0%, transparent 55%), linear-gradient(165deg, rgba(30,41,59,0.85) 0%, rgba(15,23,42,0.95) 100%)`
          : `radial-gradient(ellipse 80% 60% at top right, ${accent}18 0%, transparent 55%), linear-gradient(165deg, #ffffff 0%, #fafaf9 100%)`,
        border: `1px solid ${accent}${darkMode ? '25' : '20'}`,
        boxShadow: darkMode
          ? `inset 0 1px 0 rgba(255,255,255,0.08), 0 8px 24px -8px ${accent}25, 0 20px 48px -16px rgba(0,0,0,0.5)`
          : `inset 0 1px 0 rgba(255,255,255,1), 0 8px 24px -8px ${accent}30, 0 16px 40px -16px rgba(15,23,42,0.15)`,
      }}
    >
      <div
        className="absolute top-0 left-0 right-0 h-[2px]"
        style={{
          background: `linear-gradient(90deg, transparent 0%, ${accent} 30%, ${accent} 70%, transparent 100%)`,
          boxShadow: `0 0 12px ${accent}80`,
        }}
      />

      <div className="flex items-center justify-between mb-3">
        <span className={`am-mono text-[11px] font-bold ${muted}`}>
          #{String(index + 1).padStart(2, '0')}
        </span>
        <span
          className="am-eyebrow text-[9px] px-2 py-0.5 rounded-full"
          style={{
            background: `${accent}15`,
            color: accent,
            border: `1px solid ${accent}30`,
          }}
        >
          {tier}
        </span>
      </div>

      <h4 className={`am-serif text-lg font-medium leading-tight mb-3 ${text} line-clamp-2 min-h-[3rem]`}>
        {topic.topic}
      </h4>

      <div className="flex items-baseline justify-between mb-3">
        <div className="flex items-baseline gap-1">
          <span className={`am-numeric text-3xl ${text}`}>{topic.total}</span>
          <span className={`am-mono text-[10px] ${muted}`}>preg</span>
        </div>
        {userAcc !== null && (
          <div className="flex items-baseline gap-1">
            <span className={`am-numeric text-3xl`} style={{ color: accent }}>{userAcc}</span>
            <span className={`am-mono text-[10px] ${muted}`}>% tuyo</span>
          </div>
        )}
      </div>

      <div className={`am-mono text-[10px] flex items-center gap-2 ${subText}`}>
        <Calendar className="w-3 h-3" />
        último: {topic.lastYear?.replace('RM ', '')} · gap {topic.gap}a
      </div>

      <div className={`flex items-center justify-end mt-3 gap-1 am-mono text-[10px] uppercase tracking-widest ${muted} opacity-0 group-hover:opacity-100 transition-opacity`}>
        <span>analizar</span>
        <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-1" />
      </div>
    </motion.button>
  );
}

// ============================================================================
// SubtopicMesoCard — card por subtema en MESO
// ============================================================================
function SubtopicMesoCard({ sub, index, darkMode, onClick }) {
  const cardClass = darkMode ? 'am-card-dark' : 'am-card-light';
  const text = darkMode ? 'text-slate-100' : 'text-slate-900';
  const subText = darkMode ? 'text-slate-400' : 'text-slate-500';
  const muted = darkMode ? 'text-slate-500' : 'text-slate-400';

  const gapColor = sub.gap >= 10 ? PALETTE.danger.fill : sub.gap >= 6 ? PALETTE.warning.fill : sub.gap >= 3 ? PALETTE.info.fill : PALETTE.success.fill;
  const gapLabel = sub.gap >= 10 ? 'GAP RÉCORD' : sub.gap >= 6 ? 'GAP ALTO' : sub.gap >= 3 ? 'GAP MEDIO' : 'RECIENTE';

  return (
    <motion.button
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.04 * index }}
      whileHover={{ y: -3 }}
      onClick={onClick}
      className={`am-focus am-tier-card ${cardClass} rounded-2xl p-5 text-left relative overflow-hidden group`}
      style={{ borderTop: `2px solid ${gapColor}` }}
    >
      <div className="flex items-center justify-between mb-2">
        <span
          className="am-eyebrow text-[9px] px-2 py-0.5 rounded-full"
          style={{ background: `${gapColor}15`, color: gapColor, border: `1px solid ${gapColor}30` }}
        >
          {gapLabel}
        </span>
        <span className={`am-mono text-[10px] ${muted}`}>×{sub.count}</span>
      </div>

      <h4 className={`am-serif text-base font-medium leading-tight mb-3 ${text} line-clamp-2 min-h-[2.5rem]`}>
        {sub.subtopic}
      </h4>

      <div className="flex items-baseline gap-2 mb-2">
        <span className={`am-numeric text-4xl`} style={{ color: gapColor }}>
          {sub.gap}
        </span>
        <span className={`am-mono text-xs ${muted}`}>años sin caer</span>
      </div>

      <div className={`am-mono text-[10px] ${subText}`}>
        último RM {sub.lastYear?.replace('RM ', '')}
      </div>

      <div className={`flex items-center justify-end mt-3 gap-1 am-mono text-[10px] uppercase tracking-widest ${muted} opacity-0 group-hover:opacity-100 transition-opacity`}>
        <span>predicción</span>
        <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-1" />
      </div>
    </motion.button>
  );
}
